import React, { useContext, useState, useMemo, useCallback, useTransition, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { DataStateContext, DataActionsContext, RoleContext, AuthContext, FilterContext, SettingsContext, ClientsContext, ClientGroupsContext, DivisionLensContext, DivisionConfigContext } from "@/App";
import { itemDivision, divArea, divFlag, divTerminalKey, divStatusLabel, divStatusOptions } from "@/lib/divisions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import AttentionChip from "@/components/AttentionChip";
import PageTitle from "@/components/PageTitle";
import BlockModal from "@/components/BlockModal";
import BlockModalV2 from "@/components/BlockModalV2";
import { useBlockCatalogue, openBlocks } from "@/lib/blockCatalogue";
import AddItemModal from "@/components/AddItemModal";
import EditItemModal from "@/components/EditItemModal";
import LineItemDetailsModal from "@/components/LineItemDetailsModal";
import NextActionModal from "@/components/NextActionModal";
import StatusFilterPopover from "@/components/StatusFilterPopover";
import DueFilterPopover, { itemMatchesDueFilter, summarizeDueFilter } from "@/components/DueFilterPopover";
import { useDueFilter } from "@/contexts/DueFilterContext";
import { MANUFACTURING_STATUSES, QC_STATUSES, DISPATCH_STATUSES } from "@/components/StagePage/utils";
import MachineFloor from "@/components/MachineFloor";
import {
  EtaPickerModal,
  SpendHistoryModal,
  ReadyForMfgLightbox,
} from "@/components/ProcurementExtras";
import BulkAddModal from "@/components/BulkAddModal";
import ChildPartTracker from "@/components/bom/ChildPartTracker";
import BomCreator from "@/components/bom/BomCreator";
import { isAssembly, bomSummary } from "@/components/bom/bomUtils";
import { toast } from "@/components/ui/sonner";
import { StageRowDesktop } from "./StagePage/StageRowDesktop";
import { parseErpCodes, serializeErpCodes } from "./StagePage/StageRowDesktop";
import { StageRowMobile } from "./StagePage/StageRowMobile";
import { parseDate } from "./StagePage/utils";
import {
  Plus, Package, PackageX, Search, X, Eye, Filter, AlertTriangle,
  ArrowLeft, ArrowRight, Truck, Wrench, CheckCircle2, ShieldAlert, ClipboardCheck,
  Rows3, Rows, FileSpreadsheet, Link2, Trash2, Loader2, Sheet, Ruler,
  UserCheck, Circle, UserPlus, Receipt, Factory, Route,
} from "lucide-react";
import { RequestClientModal, ApproveClientsModal, useClientRequestCount } from "./ClientRequestModals";
import ReworkIcon from "./icons/ReworkIcon";
import StatusGlyph from "./icons/StatusGlyph";
import CncMachineIcon from "./icons/CncMachineIcon";
import RouterCardIcon from "./icons/RouterCardIcon";
import KittingTrayIcon from "./icons/KittingTrayIcon";
import { exportStageView } from "./StagePage/exportStageView";
import { StageVelocityTile, DensityHintBubble, TableSkeleton, StatChip } from "./StagePage/StageWidgets";
import { useTableDensity } from "@/hooks/useTableDensity";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { resolveClientFilterChips } from "@/components/GlobalFilterPanel";
import { useStageStats } from "./StagePage/useStageStats";
import { useStageItems } from "./StagePage/useStageItems";

const STAGE_COLORS = {
  "PO Received":    { color: "#3b82f6", bg: "rgba(59,130,246,0.15)" },
  "Procurement":    { color: "#f97316", bg: "rgba(249,115,22,0.15)" },
  "Manufacturing":  { color: "#a855f7", bg: "rgba(168,85,247,0.15)" },
  "Quality Control":{ color: "#14b8a6", bg: "rgba(20,184,166,0.15)" },
  "Dispatch":       { color: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
  "Completed":      { color: "#94a3b8", bg: "rgba(148,163,184,0.15)" },
};

const STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const API = process.env.REACT_APP_BACKEND_URL;


const PROC_TEAM_COLORS = {
  // Distinct from any stage color: stages use blue (PO), orange (Procurement),
  // purple (Mfg), teal (QC), amber (Dispatch), slate (Completed). Sub-teams
  // borrow magenta/cyan/lime — distinct hues that don't collide with stage colors.
  Material: { color: "#a21caf", bg: "rgba(162,28,175,0.10)", border: "rgba(162,28,175,0.25)", dark: "#86198f", darkest: "#581c87" },
  Tooling:  { color: "#0891b2", bg: "rgba(8,145,178,0.10)", border: "rgba(8,145,178,0.25)", dark: "#0e7490", darkest: "#155e75" },
  Gauging:  { color: "#65a30d", bg: "rgba(101,163,13,0.10)", border: "rgba(101,163,13,0.25)", dark: "#4d7c0f", darkest: "#365314" },
};

/* ── Stage velocity tile (Dispatch + QC) ─────────────────────────────────
   Matches the "Procurement Velocity" card style: white/card fill, grey border,
   monochrome labels, 3-column Today · This Week · This Month grid.          */

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function StagePage({ stage }) {
  const { items: allItems } = useContext(DataStateContext);
  // Division lens (Phase 1b): scope the whole page in one place — the item
  // table, stat chips, velocity tiles and also-in maps all derive from this.
  // Untagged legacy items read as metallic (mirrors backend DEFAULT_DIVISION).
  const { lens: divisionLens } = useContext(DivisionLensContext) || { lens: "all" };
  const { config: divisionConfig } = useContext(DivisionConfigContext) || {};
  const items = useMemo(
    () => (divisionLens === "all" ? allItems : allItems.filter(i => (i.division || "metallic") === divisionLens)),
    [allItems, divisionLens]
  );
  const {
    advanceItem,
    returnItem,
    returnItemToCustomer,
    blockItem,
    unblockItem,
    addBlock,
    clearBlock,
    updateStatus,
    createItem,
    editItem,
    deleteItem,
    patchItem,
    refreshData,
    setRouterCard,
    setKittingComplete,
    setErpCode,
  } = useContext(DataActionsContext);
  const { canEditStage, canDo } = useContext(RoleContext);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Admin";
  const { globalFilter, setGlobalFilter } = useContext(FilterContext);
  const { settings } = useContext(SettingsContext) || {};
  const { clients } = useContext(ClientsContext);
  const { clientGroups } = useContext(ClientGroupsContext);
  const dueThisWeekDays = settings?.due_this_week_days || 7;
  // Owner-first blocking (BLOCKING_REDESIGN_PLAN.md). `available` is the
  // backend's own answer — true only where BLOCKS_V2 is armed, so production
  // keeps the legacy modal until Phase 2 ships there.
  const { available: blocksV2 } = useBlockCatalogue();

  // ── Core UI state ──────────────────────────────────────────────────────────
  const [blockModalItem, setBlockModalItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  // Client creation requests — non-admins request, admins approve (PO Received)
  const [showRequestClient, setShowRequestClient] = useState(false);
  const [showApproveClients, setShowApproveClients] = useState(false);
  const [clientReqCount, refreshClientReqCount] = useClientRequestCount(stage === "PO Received" && isAdmin);
  const [editModalItem, setEditModalItem] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  // ERP code (per-part accounting code) — entry dialog, missing-code alert on
  // advance, and the transient chip highlight (erp-attention pulse).
  const [erpDialogItem, setErpDialogItem] = useState(null);
  const [erpCodes, setErpCodes] = useState([]);   // committed chips
  const [erpInput, setErpInput] = useState("");   // in-progress typing buffer
  const [erpAlertItem, setErpAlertItem] = useState(null);
  const [erpHighlightId, setErpHighlightId] = useState(null);
  // Used by the deep-link effect below.
  const location = useLocation();
  const navigate = useNavigate();
  const lastDeepLinkedRef = useRef(null);
  const [nextActionItem, setNextActionItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  // Machine Monitor (Phase 0): inline shop-floor drawer on the Manufacturing
  // stage. Phase machine: "closed" → "opening" (floor mounts behind an opaque
  // curtain + button spinner, hiding the expensive first paint) → "open"
  // (curtain fades away). While not closed the chips + line-items table are
  // unmounted so the floor renders smoothly and saves memory.
  const [shopFloorPhase, setShopFloorPhase] = useState("closed");
  const shopFloorOpen = shopFloorPhase !== "closed";
  // Maintenance discovery (v2.3): production lives on this page, so the door
  // into the Maintenance module sits beside the shop-floor toggle — carrying
  // the one number that earns a click, machines down right now.
  const showMaintenanceLink = stage === "Manufacturing" && (user?.role === "Admin" || canDo("can_view_maintenance"));
  const [machinesDown, setMachinesDown] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [advancingId, setAdvancingId] = useState(null);
  const [showDueThisWeekOnly, setShowDueThisWeekOnly] = useState(false);
  const [showReworkOnly, setShowReworkOnly] = useState(false);
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [showQcPendingOnly, setShowQcPendingOnly] = useState(false);
  // Per-column status filters for Mfg / QC / Dispatch tables. Empty array == "all".
  const [mfgStatusFilter, setMfgStatusFilter] = useState([]);
  const [qcStatusFilter, setQcStatusFilter] = useState([]);
  const [dispatchStatusFilter, setDispatchStatusFilter] = useState([]);
  // Manufacturing gate sub-filters — pending Route Card / pending Kitting.
  const [routerPendingOnly, setRouterPendingOnly] = useState(false);
  const [kittingPendingOnly, setKittingPendingOnly] = useState(false);
  // QC Quality Dossier filter — items where qc_status="Passed" but the
  // dossier sign-off flag has not been set yet.
  const [qdPendingOnly, setQdPendingOnly] = useState(false);
  const [caAcceptPendingOnly, setCaAcceptPendingOnly] = useState(false);
  const [dueFilter, setDueFilter] = useState(null);
  const [dueSort, setDueSort] = useState(null); // null | "asc" | "desc"
  const [shippedAtFilter, setShippedAtFilter] = useState(null);
  const [shippedAtSort, setShippedAtSort] = useState(() => stage === "Completed" ? "desc" : null);

  // Deep-link bridge: ?q=<part-number> in the URL → push the value into the
  // stage's local search bar so the matching row surfaces immediately.
  // Replaces an earlier ?item=<id>→modal flow that produced white-screen
  // crashes when the modal opened mid-load. The new flow is a simple state
  // mutation (no modal race) and degrades gracefully — if the item isn't on
  // this stage anymore the search just shows zero results.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (!q) return;
    const linkKey = `${location.pathname}::${q}`;
    if (lastDeepLinkedRef.current === linkKey) return;
    lastDeepLinkedRef.current = linkKey;
    setSearchQuery(q);
    // Strip ?q= so refreshing or back/forward acts on the cleaned URL.
    params.delete("q");
    const nextUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    navigate(nextUrl, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Command Palette: apply incoming search term to this stage's search box
  useEffect(() => {
    const term = location.state?.search;
    if (!term) return;
    setSearchQuery(term);
    navigate(location.pathname, { replace: true, state: {} }); // clear so it doesn't re-fire
  }, [location.state, location.pathname, navigate]); // eslint-disable-line react-hooks/exhaustive-deps


  // Sync this page's due filter + active stage up to the global DueFilterContext
  // so the pipeline nav in Layout.js can show per-stage counts for the same
  // filter. Clear the global filter when the user navigates away from a stage.
  const { setDueFilter: setGlobalDueFilter, setActiveStage: setGlobalActiveStage } = useDueFilter();
  const dueFilterSyncedRef = useRef(false);
  useEffect(() => {
    setGlobalActiveStage(stage);
    return () => {
      setGlobalActiveStage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  useEffect(() => {
    if (!dueFilterSyncedRef.current) { dueFilterSyncedRef.current = true; return; }
    setGlobalDueFilter(dueFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueFilter]);
  const { density, setDensity, config: densityCfg } = useTableDensity();
  // Brief transition state when the user switches density — gives the UI a beat
  // to swap dropdown sizes, column labels, and spend placement without flashing.
  const [densitySwitching, setDensitySwitching] = useState(false);
  // First-time discovery hints for the density toggle.
  //   "try-compact"  → nudge the user toward Compact (shown while comfortable + never tried)
  //   "switch-back"  → right after they try Compact, remind them they can flip back
  //   null           → nothing
  const [densityHint, setDensityHint] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const compactTried = window.localStorage.getItem("rocklink.densityHint.compactTried") === "1";
      if (!compactTried && density === "comfortable") setDensityHint("try-compact");
    } catch (_e) { /* ignore */ }
    // Only run once on mount. Intentional — do not react to density changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dismissHint = useCallback(() => {
    setDensityHint(prev => {
      // Manually dismissing the "try-compact" hint counts as an acknowledgement —
      // persist so we don't nag them on the next page load. The "switch-back"
      // hint is already transient so no persistence is needed.
      if (prev === "try-compact") {
        try { window.localStorage.setItem("rocklink.densityHint.compactTried", "1"); } catch (_e) { /* ignore */ }
      }
      return null;
    });
  }, []);
  const handleDensityChange = useCallback((next) => {
    if (next === density) return;
    setDensitySwitching(true);
    setDensity(next);
    setTimeout(() => setDensitySwitching(false), 1000);
    // Hint flow: first time they land on Compact, flip the hint to show the
    // "switch back any time" reminder. Persist the dismissal so we never
    // badger them again.
    if (next === "compact") {
      try {
        const alreadyTried = window.localStorage.getItem("rocklink.densityHint.compactTried") === "1";
        window.localStorage.setItem("rocklink.densityHint.compactTried", "1");
        if (!alreadyTried) {
          setDensityHint("switch-back");
          // Auto-dismiss the reminder after 10s so it doesn't linger.
          setTimeout(() => setDensityHint(h => (h === "switch-back" ? null : h)), 10000);
        } else {
          setDensityHint(null);
        }
      } catch (_e) { setDensityHint(null); }
    } else {
      // Flipping back to comfortable also clears any reminder.
      setDensityHint(null);
    }
  }, [density, setDensity]);

  // ── Return reason modal ────────────────────────────────────────────────────
  const [returnReasonItem, setReturnReasonItem] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnIsRework, setReturnIsRework] = useState(false);
  const [returnDrawingLinks, setReturnDrawingLinks] = useState([""]); // start with one empty input
  const [returningId, setReturningId] = useState(null);
  // QC-failed return-to-customer modal
  const [rtcItem, setRtcItem] = useState(null);
  const [rtcValue, setRtcValue] = useState("0");
  const [rtcNote, setRtcNote] = useState("");
  const [rtcSaving, setRtcSaving] = useState(false);

  // ── Procurement state ──────────────────────────────────────────────────────
  // Pending ETA capture — populated when a subteam status flips to "Ordered".
  const [pendingEtaChange, setPendingEtaChange] = useState(null);
  // Item whose spend audit history is open in the lightbox.
  const [spendHistoryItem, setSpendHistoryItem] = useState(null);
  // Manual lightbox trigger for the "Ready for Manufacturing" alert.
  const [readyForMfgOpen, setReadyForMfgOpen] = useState(false);
  // Assemblies — which line's Child Part Tracker / parts-list creator is open
  // (ids, so the modal always renders the LIVE item from global state), the
  // back-to-single confirmation, and the partially-procured advance dialog.
  const [bomTrackerId, setBomTrackerId] = useState(null);
  const [bomCreatorId, setBomCreatorId] = useState(null);
  const [bomToSingleItem, setBomToSingleItem] = useState(null);
  const [partialAdvanceItem, setPartialAdvanceItem] = useState(null);
  const [procSubteamFilter, setProcSubteamFilter] = useState(null);

  // ── Manufacturing workflow state ───────────────────────────────────────────
  const [mfgAdvanceItem, setMfgAdvanceItem] = useState(null);
  const [mfgMachiningItem, setMfgMachiningItem] = useState(null);
  const [mfgMachineUsed, setMfgMachineUsed] = useState("");
  const [mfgCycleTime, setMfgCycleTime] = useState("");

  // ── Dispatch workflow state ────────────────────────────────────────────────
  const [dispatchShippedItem, setDispatchShippedItem] = useState(null);
  const [dispatchCarrier, setDispatchCarrier] = useState("");
  const [dispatchTrackingId, setDispatchTrackingId] = useState("");
  const [dispatchShippedAt, setDispatchShippedAt] = useState(() => new Date().toISOString().slice(0, 10));

  // ── Third Party workflow state (Manufacturing) ─────────────────────────────
  const [thirdPartyItem, setThirdPartyItem] = useState(null);
  const [thirdPartyWhere, setThirdPartyWhere] = useState("");
  const [thirdPartyEta, setThirdPartyEta] = useState("");

  // ── Deferred rendering (useTransition) ────────────────────────────────────
  const [isPending, startTransition] = useTransition();
  const [visibleItems, setVisibleItems] = useState(null); // null = initial load skeleton

  // Mount ONLY the active layout (perf, 17-Jul) — desktop table vs mobile cards,
  // instead of both. Kills the "double mount" of ~100 invisible rows per switch.
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Commit-hold ("animation wins", 17-Jul): on a STAGE SWITCH, hold the heavy
  // row commit until the ~0.48s rail-arrow stretch + accent swipe finish, so the
  // animation runs on a free main thread. Same-stage changes (filters/search/
  // edits) commit immediately. Performance Mode + reduced-motion skip the hold.
  const HOLD_MS = 520; // rail stretch: tail 0.04s delay + 0.44s, + buffer (header redesign, 2-Nov-2026)
  const [holding, setHolding] = useState(false);
  const prevStageRef = useRef(stage);
  const holdTimerRef = useRef(null);
  useEffect(() => () => { if (holdTimerRef.current) clearTimeout(holdTimerRef.current); }, []);

  // ── Derived constants ──────────────────────────────────────────────────────
  // canEdit: user must have both stage edit access AND the can_edit_items feature permission
  const canEdit = canEditStage(stage);
  // ERP code entry: in Manufacturing it follows normal stage-edit rights; in
  // QC/Dispatch/Completed only Admin + Manufacturing editors may fill a blank
  // code (the exception path — QC/Dispatch roles stay locked out, mirroring
  // the backend rule). Changing an existing code stays Admin-only in the chip.
  const canErpEdit = stage === "Manufacturing" ? canEdit : (isAdmin || canEditStage("Manufacturing"));
  const canEditItemDetails = canEdit && canDo("can_edit_items");
  const showValue = canDo("can_view_value");
  const showSpend = canDo("can_view_spend");
  // Child Part Tracker rights (mirror backend _bom_can_buy / _bom_can_define):
  // Material buys; PO Received or Material define the list.
  const canBuyBom = useMemo(() => {
    if (!canEditStage("Procurement")) return false;
    const subs = user?.permissions?.procurement_subteams;
    return user?.role !== "Procurement" || !Array.isArray(subs) || !subs.length || subs.includes("Material");
  }, [canEditStage, user]);
  const canDefineBom = canEditStage("PO Received") || canBuyBom;
  const canDelete = canDo("can_delete");
  const sc = STAGE_COLORS[stage];
  const stageIdx = STAGES.indexOf(stage);
  const isLastStage = stageIdx === STAGES.length - 1;
  const isFirstStage = stageIdx === 0;

  // Stage accent swipe direction — the top colour rule fills IN from the side
  // the pipeline chevron travelled from, so the two motions read as one gesture:
  // moving downstream (higher index) fills left→right, upstream fills right→left.
  // Refs (not state) so the value is ready on the first painted frame; guarded
  // to only re-evaluate when the stage actually changes. StagePage re-renders
  // (never remounts) across stage routes, so prevStageIdxRef persists.
  const prevStageIdxRef = useRef(-2);
  const accentSwipeDirRef = useRef("ltr");
  if (prevStageIdxRef.current !== stageIdx) {
    accentSwipeDirRef.current = (prevStageIdxRef.current === -2 || stageIdx >= prevStageIdxRef.current) ? "ltr" : "rtl";
    prevStageIdxRef.current = stageIdx;
  }
  const accentSwipeDir = accentSwipeDirRef.current;

  // ── Step 3: Memoised client → color lookup ─────────────────────────────────
  

  

  // ── Filtered & sorted items ────────────────────────────────────────────────
    // Division lens → status-chip base list: the historic metallic list in the
    // All lens (behaviour unchanged), the scoped division's own keys otherwise.
    const lensStatusKeys = useMemo(() => {
      if (divisionLens === "all") return null; // hook falls back to metallic constants
      const area = stage === "Manufacturing" ? "manufacturing"
                 : stage === "Quality Control" ? "qc"
                 : stage === "Dispatch" ? "dispatch" : null;
      if (!area) return null;
      return divStatusOptions(divisionConfig, divisionLens, area).map(s => s.key);
    }, [divisionLens, divisionConfig, stage]);
    const { clientColorMap, clientLogoMap, partDuplicateMap, dueThisWeekItems, reworkStageItems, blockedStageItems, qcPendingCount, stageScopedItems, overdueCount, statusCounts } = useStageStats({ items, stage, globalFilter, clients, clientGroups, dueThisWeekDays, statusKeys: lensStatusKeys });

  

  

  

  

  

  // Count of items where QC status is the "pending" one (Pending Inspection)
  

  // Stage-scoped dataset honouring global (client/priority) filters — reused by
  // overdue count and per-status breakdown stats.
  

  // Items whose due date is strictly before today (still sitting in this stage).
  

  // Per-status counts for stages that expose a status column
  

    const stageItems = useStageItems({ items, stage, searchQuery, globalFilter, showDueThisWeekOnly, showReworkOnly, showBlockedOnly, showQcPendingOnly, procSubteamFilter, dueThisWeekDays, mfgStatusFilter, qcStatusFilter, dispatchStatusFilter, routerPendingOnly, kittingPendingOnly, qdPendingOnly, caAcceptPendingOnly, dueFilter, dueSort, shippedAtFilter, shippedAtSort });

  const totalItems = items.filter(i => i.stage === stage).length;
  const filteredByGlobal = globalFilter.clients != null || globalFilter.priority !== "all" || globalFilter.flag != null;

  const dispatchVelocity = useMemo(() => {
    if (stage !== "Dispatch") return { today: 0, week: 0, month: 0 };
    const now = new Date();
    const cy = now.getFullYear(), cm = now.getMonth(), cd = now.getDate();
    // Monday of current week
    const dow = now.getDay();
    const weekStart = new Date(now);
    weekStart.setDate(cd - ((dow + 6) % 7));
    weekStart.setHours(0, 0, 0, 0);
    let today = 0, week = 0, month = 0;
    for (const item of items) {
      if (!item.shipped_at) continue;
      const isShipped = item.stage === "Completed"
        || (item.stage === "Dispatch" && (item.dispatch_status === "Shipped" || item.dispatch_status === "Delivered"));
      if (!isShipped) continue;
      const d = new Date(item.shipped_at);
      if (d.getFullYear() === cy && d.getMonth() === cm) {
        month++;
        if (d >= weekStart) week++;
        if (d.getDate() === cd) today++;
      }
    }
    return { today, week, month };
  }, [stage, items]);

  const qcVelocity = useMemo(() => {
    if (stage !== "Quality Control") return { today: 0, week: 0, month: 0 };
    const now = new Date();
    const cy = now.getFullYear(), cm = now.getMonth(), cd = now.getDate();
    const dow = now.getDay();
    const weekStart = new Date(now);
    weekStart.setDate(cd - ((dow + 6) % 7));
    weekStart.setHours(0, 0, 0, 0);
    let today = 0, week = 0, month = 0;
    for (const item of items) {
      const entered = item.stage_ages?.["Dispatch"]?.entered_at;
      if (!entered) continue;
      const d = new Date(entered);
      if (d.getFullYear() === cy && d.getMonth() === cm) {
        month++;
        if (d >= weekStart) week++;
        if (d.getDate() === cd) today++;
      }
    }
    return { today, week, month };
  }, [stage, items]);

  const procPending = useMemo(() => {
    if (stage !== "Procurement") return null;
    const base = items.filter(i => {
      if (i.stage !== "Procurement") return false;
      if (globalFilter.clients != null && !globalFilter.clients.includes(i.client)) return false;
      if (globalFilter.priority !== "all" && i.priority !== globalFilter.priority) return false;
      return true;
    });
    const tally = (field) => {
      const out = { pending: 0, ordered: 0, complete: 0 };
      for (const i of base) {
        const s = i[field] || "Pending";
        if (s === "Pending") out.pending += 1;
        else if (s === "Ordered" || s === "Partially Received") out.ordered += 1;
        else if (s === "Complete") out.complete += 1;
      }
      return out;
    };
    return {
      material: tally("material_status"),
      tooling:  tally("tooling_status"),
      gauging:  tally("gauging_status"),
      total: base.length,
    };
  }, [items, stage, globalFilter]);

  // ── Ready-for-Mfg items (all 3 subteams Complete, not blocked) ────────────
  const readyItems = useMemo(() => {
    if (stage !== "Procurement") return [];
    return items.filter(i =>
      i.stage === "Procurement" &&
      !i.is_blocked &&
      i.material_status === "Complete" &&
      i.tooling_status  === "Complete" &&
      i.gauging_status  === "Complete"
    );
  }, [items, stage]);
  const readyCount = readyItems.length;

  // ── Procurement velocity — items that entered Manufacturing this period ────
  const velocity = useMemo(() => {
    if (stage !== "Procurement") return { today: 0, week: 0, month: 0 };
    const now = new Date();
    const startToday = new Date(now); startToday.setHours(0,0,0,0);
    const dow = now.getDay();
    const daysSinceMon = (dow + 6) % 7;
    const startWeek = new Date(startToday);
    startWeek.setDate(startWeek.getDate() - daysSinceMon);
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let today = 0, week = 0, month = 0;
    for (const it of items) {
      const enteredIso = it?.stage_ages?.["Manufacturing"]?.entered_at;
      if (!enteredIso) continue;
      if (it.stage === "PO Received" || it.stage === "Procurement") continue;
      const t = new Date(enteredIso);
      if (Number.isNaN(t.getTime())) continue;
      if (t >= startMonth) month++;
      if (t >= startWeek)  week++;
      if (t >= startToday) today++;
    }
    return { today, week, month };
  }, [items, stage]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const pageInitialized = useRef(false);

  // Machine Monitor: toggle the inline shop-floor drawer through the curtain
  // phases. Opening shows the curtain (+ button spinner) while the floor mounts
  // behind it, then reveals after a short beat; closing collapses immediately.
  const shopFloorTimer = useRef(null);
  const toggleShopFloor = useCallback(() => {
    setShopFloorPhase(phase => {
      if (phase === "closed") {
        if (shopFloorTimer.current) clearTimeout(shopFloorTimer.current);
        shopFloorTimer.current = setTimeout(() => setShopFloorPhase("open"), 600);
        return "opening";
      }
      if (phase === "open") {
        // closing: drop a curtain over the floor while the chips + table remount
        // underneath, then collapse — mirrors the open so the reflow is hidden.
        if (shopFloorTimer.current) clearTimeout(shopFloorTimer.current);
        shopFloorTimer.current = setTimeout(() => setShopFloorPhase("closed"), 600);
        return "closing";
      }
      // mid-transition (opening/closing): cancel and snap to closed
      if (shopFloorTimer.current) { clearTimeout(shopFloorTimer.current); shopFloorTimer.current = null; }
      return "closed";
    });
  }, []);
  useEffect(() => () => { if (shopFloorTimer.current) clearTimeout(shopFloorTimer.current); }, []);

  useEffect(() => {
    if (!showMaintenanceLink) return undefined;
    let cancelled = false;
    const read = () => axios.get(`${API}/api/maintenance/counts`)
      .then(r => {
        if (cancelled) return;
        const c = r.data || {};
        // One open ticket per machine, so open tickets ARE machines down.
        setMachinesDown((c.received || 0) + (c.repair || 0) + (c.handover || 0));
      })
      .catch(() => { /* the button still works, it just carries no number */ });
    read();
    const id = setInterval(read, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [showMaintenanceLink]);

  useEffect(() => {
    if (!pageInitialized.current && settings?.default_page_size) {
      setItemsPerPage(settings.default_page_size);
      pageInitialized.current = true;
    }
  }, [settings?.default_page_size]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 7: Pagination reset — stage navigation and search ────────────────
  // Resets when switching stages OR when a search query is typed (so results
  // always appear from page 1 instead of a now-empty higher page).
  useEffect(() => { setCurrentPage(1); }, [stage, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2: Keep previous items dimmed while next stage loads ─────────────
  // visibleItems stays non-null after first load → old items remain visible
  // (opacity 0.45 via isPending/holding) during stage transition instead of a
  // skeleton. On a STAGE SWITCH the commit is HELD until the glide finishes so
  // the animation stays smooth; same-stage changes (filters/search) commit now;
  // Performance Mode / reduced-motion never hold (they have no glide to protect).
  useEffect(() => {
    const isStageSwitch = prevStageRef.current !== stage;
    prevStageRef.current = stage;
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    const noGlide =
      (typeof document !== "undefined" && document.documentElement.dataset.flat === "1") ||
      (typeof document !== "undefined" && document.hidden) || // tab backgrounded → no visible glide to protect (and setTimeout is throttled)
      (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (isStageSwitch && !noGlide) {
      setHolding(true);
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        setHolding(false);
        startTransition(() => setVisibleItems(stageItems));
      }, HOLD_MS);
    } else {
      setHolding(false);
      startTransition(() => setVisibleItems(stageItems));
    }
  }, [stageItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInitialLoad = visibleItems === null;
  const displayItems = visibleItems ?? [];

  // Any column/row filter currently narrowing the table? When true we always
  // render the table header + filter popovers even if 0 items match — so the
  // user can actually *clear* the filter without navigating away.
  const hasActiveFilters = !!(
    searchQuery ||
    globalFilter ||
    showDueThisWeekOnly ||
    showReworkOnly ||
    showBlockedOnly ||
    showQcPendingOnly ||
    procSubteamFilter ||
    (mfgStatusFilter && mfgStatusFilter.length) ||
    (qcStatusFilter && qcStatusFilter.length) ||
    (dispatchStatusFilter && dispatchStatusFilter.length) ||
    dueFilter
  );

  const paginatedItems = useMemo(() => {
    if (itemsPerPage === 0) return displayItems;
    return displayItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [displayItems, currentPage, itemsPerPage]);

  const totalPages = itemsPerPage === 0 ? 1 : Math.ceil(displayItems.length / itemsPerPage);
  const showingFrom = displayItems.length === 0 ? 0 : (itemsPerPage === 0 ? 1 : (currentPage - 1) * itemsPerPage + 1);
  const showingTo = itemsPerPage === 0 ? displayItems.length : Math.min(currentPage * itemsPerPage, displayItems.length);

  // ── Step 4: All handlers wrapped in useCallback ────────────────────────────
  // Advance gates — config-driven per the item's division (Phase 1b). The
  // metallic config mirrors the historic hardcoded checks exactly; drilling
  // differs per MULTI_DIVISION_PLAN §1b. Must stay in lockstep with the
  // backend advance endpoint's gate block.
  const _SUBTEAM_FIELD = { Material: "material_status", Tooling: "tooling_status", Gauging: "gauging_status" };
  const canAdvance = useCallback((item) => {
    if (item.is_blocked) return false;
    if (isLastStage) return false;
    const div = itemDivision(item);
    const asm = isAssembly(item);
    const bs = bomSummary(item.bom);
    if (stage === "PO Received") {
      if (divFlag(divisionConfig, div, "po_received", "design_approval_gate", false) && !item.design_approved) return false;
      if (asm && !bs.n) return false;
    }
    if (stage === "Procurement") {
      const subteams = divArea(divisionConfig, div, "procurement").subteams || ["Material", "Tooling", "Gauging"];
      const term = divTerminalKey(divisionConfig, div, "procurement", "Complete");
      // Assemblies: Material is judged by the parts list — 1+ part in lets it
      // move on partially procured (after a confirmation).
      for (const st of subteams) {
        if (asm && st === "Material") { if (!bs.received) return false; continue; }
        if (item[_SUBTEAM_FIELD[st]] !== term) return false;
      }
    }
    if (stage === "Manufacturing" && asm && bs.outstanding) return false;
    if (stage === "Manufacturing" && item.manufacturing_status !== divTerminalKey(divisionConfig, div, "manufacturing", "Machining Complete")) return false;
    if (stage === "Dispatch" && item.dispatch_status !== "Shipped") return false;
    if (stage === "Quality Control") {
      if (item.qc_status !== "Passed") return false;
      if (divFlag(divisionConfig, div, "qc", "qd", true) && !item.quality_dossier_ready) return false;
      if (divFlag(divisionConfig, div, "qc", "customer_acceptance", true) && !item.customer_acceptance) return false;
      if (divFlag(divisionConfig, div, "qc", "coc_gate", false) && item.coc_status !== "Complete") return false;
    }
    return true;
  }, [isLastStage, stage, divisionConfig]);

  // Short, dignified one-liners shown in the locked-Advance tooltip (hover +
  // tap). Kept terse on purpose — the tooltip is a glance, not a sentence; the
  // premium look depends on these never wrapping or truncating.
  const getAdvanceLockReason = useCallback((item) => {
    if (item.is_blocked) return "Blocked — resolve the problem first";
    if (isLastStage) return "Already at the final stage";
    const div = itemDivision(item);
    const asm = isAssembly(item);
    const bs = bomSummary(item.bom);
    if (stage === "PO Received") {
      if (divFlag(divisionConfig, div, "po_received", "design_approval_gate", false) && !item.design_approved) {
        return "Customer design approval pending";
      }
      if (asm && !bs.n) return "Add the child parts first";
    }
    if (stage === "Procurement") {
      const subteams = divArea(divisionConfig, div, "procurement").subteams || ["Material", "Tooling", "Gauging"];
      const term = divTerminalKey(divisionConfig, div, "procurement", "Complete");
      const pending = subteams.filter(st => asm && st === "Material" ? !bs.received : item[_SUBTEAM_FIELD[st]] !== term);
      if (pending.length > 0) {
        if (asm && pending.length === 1 && pending[0] === "Material") return "No child parts received yet";
        return `Waiting on ${pending.join(", ")}`;
      }
    }
    if (stage === "Manufacturing" && asm && bs.outstanding) {
      return `${bs.outstanding} child part${bs.outstanding === 1 ? "" : "s"} still to come`;
    }
    if (stage === "Manufacturing") {
      const term = divTerminalKey(divisionConfig, div, "manufacturing", "Machining Complete");
      if (item.manufacturing_status !== term) {
        return div === "drilling" ? `Finish '${divStatusLabel(divisionConfig, div, "manufacturing", term)}' first` : "Finish machining first";
      }
    }
    if (stage === "Dispatch" && item.dispatch_status !== "Shipped") {
      return "Mark as shipped first";
    }
    if (stage === "Quality Control") {
      if (item.qc_status !== "Passed") return "Pass QC first";
      if (divFlag(divisionConfig, div, "qc", "qd", true) && !item.quality_dossier_ready) return "Quality dossier pending";
      if (divFlag(divisionConfig, div, "qc", "customer_acceptance", true) && !item.customer_acceptance) return "Customer acceptance pending";
      if (divFlag(divisionConfig, div, "qc", "coc_gate", false) && item.coc_status !== "Complete") return "COC pending";
    }
    return "";
  }, [isLastStage, stage, divisionConfig]);

  // ── Step 1: Advance — play wipe animation, then hand off to the mutation
  // which optimistically moves the row out of this stage's filter. The 520ms
  // delay matches the CSS animation so the row finishes sweeping away *before*
  // it disappears from the list; the destination tab's "+1" badge then pops
  // ~2s later, ~70ms after the item lands in the new stage — satisfying beat.
  const handleAdvanceWithAnimation = useCallback(async (itemId) => {
    setAdvancingId(itemId);
    await new Promise(r => setTimeout(r, 440)); // 80% of 550ms animation — row fades at 85%
    setAdvancingId(null);
    advanceItem(itemId);  // fire-and-forget: the action context handles errors + toast
  }, [advanceItem]);

  // Unified advance handler — routes PO Received and Manufacturing to modals
  const handleAdvanceClick = useCallback((item) => {
    if (stage === "PO Received") {
      if (!canAdvance(item)) return;
      setMfgAdvanceItem({ ...item, _isAccept: true });
    } else if (stage === "Manufacturing") {
      if (!canAdvance(item)) return;
      // Soft gate: nudge for a missing ERP code before the advance check.
      // "Don't update" continues; it never blocks the advance.
      if (!item.erp_code) {
        setErpAlertItem(item);
        setErpHighlightId(item.id);
        return;
      }
      setMfgAdvanceItem(item);
    } else if (stage === "Procurement" && isAssembly(item) && bomSummary(item.bom).outstanding) {
      if (!canAdvance(item)) return;
      setPartialAdvanceItem(item);
    } else {
      handleAdvanceWithAnimation(item.id);
    }
  }, [stage, canAdvance, handleAdvanceWithAnimation]);

  const handlePartialAdvanceConfirm = useCallback(async () => {
    const item = partialAdvanceItem;
    setPartialAdvanceItem(null);
    if (!item) return;
    setAdvancingId(item.id);
    await new Promise(r => setTimeout(r, 440));
    setAdvancingId(null);
    advanceItem(item.id, { partialOk: true });
  }, [partialAdvanceItem, advanceItem]);

  // ── Assemblies ─────────────────────────────────────────────────────────────
  // Empty list + allowed to add → straight to the creator; otherwise the tracker.
  const handleOpenBom = useCallback((item) => {
    if (!bomSummary(item.bom).n && canDefineBom) setBomCreatorId(item.id);
    else setBomTrackerId(item.id);
  }, [canDefineBom]);

  const saveBuildType = useCallback(async (item, buildType) => {
    try {
      const res = await axios.put(`${API}/api/line-items/${item.id}/build-type`, { build_type: buildType });
      patchItem(item.id, res.data);
      if (buildType === "assembly") {
        toast.success("Marked as an assembly — add its child parts");
        if (!bomSummary(res.data.bom).n) setBomCreatorId(item.id);
      } else {
        toast.success("Back to a single part");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change the build type");
    }
  }, [patchItem]);

  const handleBuildTypeChange = useCallback((item, buildType) => {
    if (buildType === "single" && bomSummary(item.bom).n) { setBomToSingleItem(item); return; }
    saveBuildType(item, buildType);
  }, [saveBuildType]);

  const handleBomItemUpdate = useCallback((updated) => {
    if (updated?.id) patchItem(updated.id, updated);
  }, [patchItem]);

  // ── ERP code handlers ──────────────────────────────────────────────────────
  const handleErpChipClick = useCallback((item) => {
    if (!(item.part_number || "").trim()) {
      toast.error("This item has no part number — add one before setting an ERP code");
      return;
    }
    setErpCodes(parseErpCodes(item.erp_code || ""));
    setErpInput("");
    setErpDialogItem(item);
  }, []);

  // Commit whatever is in the typing buffer into chips. Splits on
  // whitespace/comma, drops codes < 3 chars and exact duplicates, and
  // (for a multi-token paste) reports what was added vs skipped.
  const commitErpInput = useCallback((raw) => {
    const tokens = parseErpCodes(raw);
    if (tokens.length === 0) return true;   // nothing to add; clear buffer
    let added = 0, dupes = 0, tooShort = 0;
    setErpCodes(prev => {
      const seen = new Set(prev.map(c => c.toUpperCase()));
      const next = [...prev];
      for (const t of tokens) {
        if (t.length < 3) { tooShort++; continue; }
        const key = t.toUpperCase();
        if (seen.has(key)) { dupes++; continue; }
        seen.add(key); next.push(t); added++;
      }
      return next;
    });
    // Toast only for multi-token commits (paste); single typed codes stay quiet.
    if (tokens.length > 1) {
      if (added === 0 && (dupes || tooShort)) {
        toast.warning("No codes added", { description: [dupes && `${dupes} duplicate${dupes > 1 ? "s" : ""}`, tooShort && `${tooShort} too short (min 3 chars)`].filter(Boolean).join(" · ") });
      } else {
        const notes = [dupes && `${dupes} duplicate${dupes > 1 ? "s" : ""} skipped`, tooShort && `${tooShort} too short (min 3 chars)`].filter(Boolean).join(" · ");
        toast.success(`Added ${added} code${added !== 1 ? "s" : ""}`, notes ? { description: notes } : undefined);
      }
    } else if (added === 0 && tokens[0]?.length < 3) {
      toast.error("ERP code must be at least 3 characters");
      return false;   // keep the buffer so the user can fix it
    }
    return true;
  }, []);

  const handleErpInputChange = useCallback((e) => {
    const v = e.target.value;
    // A separator keystroke (space/comma/newline) commits the token before it.
    if (/[\s,]/.test(v)) {
      const ok = commitErpInput(v);
      setErpInput(ok ? "" : v.replace(/[\s,]+$/, ""));
    } else {
      setErpInput(v);
    }
  }, [commitErpInput]);

  const handleErpInputKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (erpInput.trim()) { if (commitErpInput(erpInput)) setErpInput(""); }
    } else if (e.key === "Backspace" && !erpInput) {
      setErpCodes(prev => prev.slice(0, -1));   // backspace on empty removes last chip
    }
  }, [erpInput, commitErpInput]);

  const handleErpPaste = useCallback((e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (/[\s,]/.test(text)) {   // multi-code paste → split into chips
      e.preventDefault();
      if (commitErpInput(text)) setErpInput("");
    }
  }, [commitErpInput]);

  const removeErpCode = useCallback((idx) => {
    setErpCodes(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleErpSave = useCallback(async () => {
    const item = erpDialogItem;
    // Fold any un-committed buffer text into the chips before saving.
    const pending = parseErpCodes(erpInput);
    const seen = new Set(erpCodes.map(c => c.toUpperCase()));
    const merged = [...erpCodes];
    for (const t of pending) { if (t.length >= 3 && !seen.has(t.toUpperCase())) { seen.add(t.toUpperCase()); merged.push(t); } }
    const value = serializeErpCodes(merged);
    if (merged.length === 0) {
      // Empty = clear. Backend allows clear for Admin only; let it enforce.
      try { await setErpCode(item.id, ""); setErpDialogItem(null); setErpHighlightId(null); } catch { /* toast by mutation */ }
      return;
    }
    try {
      await setErpCode(item.id, value);
      setErpDialogItem(null);
      setErpHighlightId(null);
    } catch { /* toast shown by the mutation */ }
  }, [erpDialogItem, erpCodes, erpInput, setErpCode]);

  // Alert → "Update ERP code": close the alert and open the entry dialog.
  const handleErpAlertUpdate = useCallback(() => {
    const item = erpAlertItem;
    setErpAlertItem(null);
    if (item) {
      setErpCodes(parseErpCodes(item.erp_code || ""));
      setErpInput("");
      setErpDialogItem(item);
    }
  }, [erpAlertItem]);

  // Alert → "Don't update": proceed to the normal Manufacturing advance check.
  const handleErpAlertSkip = useCallback(() => {
    const item = erpAlertItem;
    setErpAlertItem(null);
    setErpHighlightId(null);
    if (item) setMfgAdvanceItem(item);
  }, [erpAlertItem]);

  // The chip pulse is transient — clear it after the animation has played.
  useEffect(() => {
    if (!erpHighlightId) return;
    const t = setTimeout(() => setErpHighlightId(null), 6000);
    return () => clearTimeout(t);
  }, [erpHighlightId]);

  // Lines sharing the dialog item's part number (for the "applies to N lines"
  // copy). partDuplicateMap only tracks parts with >1 occurrence → fallback 1.
  const erpLineCount = useMemo(() => {
    const pn = (erpDialogItem?.part_number || "").trim();
    if (!pn) return 1;
    return partDuplicateMap?.get(pn)?.length || 1;
  }, [erpDialogItem, partDuplicateMap]);

  const handleAdvanceDocsConfirm = useCallback(() => {
    const item = mfgAdvanceItem;
    setMfgAdvanceItem(null);
    if (!item._isAccept && item.manufacturing_status === "Machining Complete") {
      setMfgMachiningItem(item);
      setMfgMachineUsed(item.machine_used || "");
      setMfgCycleTime(item.cycle_time || "");
    } else {
      handleAdvanceWithAnimation(item.id);
    }
  }, [mfgAdvanceItem, handleAdvanceWithAnimation]);

  const handleMfgMachiningConfirm = useCallback(async () => {
    const item = mfgMachiningItem;
    setMfgMachiningItem(null);
    if (mfgMachineUsed || mfgCycleTime) {
      await editItem(item.id, { machine_used: mfgMachineUsed, cycle_time: mfgCycleTime });
    }
    handleAdvanceWithAnimation(item.id);
    setMfgMachineUsed(""); setMfgCycleTime("");
  }, [mfgMachiningItem, mfgMachineUsed, mfgCycleTime, editItem, handleAdvanceWithAnimation]);

  const handleOpenReturn = useCallback((item) => {
    setReturnReasonItem(item);
    setReturnReason("");
    setReturnIsRework(false);
    setReturnDrawingLinks([""]);
  }, []);

  const handleOpenReturnToCustomer = useCallback((item) => {
    setRtcItem(item);
    setRtcValue("0");
    setRtcNote("Failed QC — being returned to customer.");
  }, []);

  const handleOpenBlock = useCallback((item) => setBlockModalItem(item), []);
  // Party names already used on this board — feeds the supplier/lab/vendor
  // autocomplete so the same vendor isn't spelled three ways (plan §6.3).
  const partySuggestions = useMemo(() => {
    const seen = new Set();
    items.forEach(i => (i.blocks || []).forEach(b => {
      const p = (b?.party || "").trim();
      if (p && b.owner_type === "external") seen.add(p);
    }));
    return Array.from(seen).sort();
  }, [items]);
  const handleOpenEdit  = useCallback((item) => setEditModalItem(item), []);
  const handleDetailsClick    = useCallback((it) => setDetailsItem(it), []);
  const handleNextActionClick = useCallback((it) => setNextActionItem(it), []);
  // With blocks v2 armed, an item can hold several blocks — clearing then means
  // "which one?", so the shield opens the modal instead of wiping all of them.
  // One block (or the legacy backend) keeps the old one-click unblock.
  const handleUnblock = useCallback((itemId) => {
    const it = items.find(i => i.id === itemId);
    if (blocksV2 && it && openBlocks(it).length > 1) { setBlockModalItem(it); return; }
    unblockItem(itemId);
  }, [unblockItem, items, blocksV2]);

  const handleDeleteClick = useCallback((itemId) => {
    if (!canDelete) { toast.error("Sorry, you do not have permission to delete items"); return; }
    setConfirmDeleteId(prev => prev === itemId ? null : itemId);
  }, [canDelete]);

  const handleDeleteConfirm = useCallback(async () => {
    if (confirmDeleteId) { await deleteItem(confirmDeleteId); setConfirmDeleteId(null); }
  }, [confirmDeleteId, deleteItem]);

  const handleDeleteConfirmInline = useCallback((itemId) => {
    deleteItem(itemId);
    setConfirmDeleteId(null);
  }, [deleteItem]);

  const handleDeleteCancel = useCallback(() => setConfirmDeleteId(null), []);

  const handleProcStatusChange = useCallback((item, field, taskName, newValue) => {
    if (newValue === "Ordered") {
      // Need an ETA before committing the status change. Capture it via the
      // EtaPickerModal — confirm/cancel handlers route to updateStatus.
      setPendingEtaChange({ item, field, taskName, newValue });
    } else {
      // Pending / Partially Received / Complete don't need an ETA. Pass empty
      // string so any stale ETA from a previous "Ordered" cycle is cleared.
      updateStatus(item.id, field, newValue, "");
    }
  }, [updateStatus]);

  const handleEtaConfirm = useCallback(async ({ eta, spendUsd, spendOriginal, spendCurrency }) => {
    if (!pendingEtaChange) return;
    const { item, field, taskName, newValue } = pendingEtaChange;
    setPendingEtaChange(null);
    // 1) flip the status with the captured ETA
    await updateStatus(item.id, field, newValue, eta || "");
    // 2) record the spend (zero is allowed — skip the API hit only when 0)
    if (spendUsd > 0) {
      try {
        const note = spendCurrency === "INR"
          ? `Order spend ₹${Number(spendOriginal).toLocaleString("en-IN", { maximumFractionDigits: 0 })} @ ${eta ? `ETA ${eta}` : "no ETA"}`
          : `Order spend @ ${eta ? `ETA ${eta}` : "no ETA"}`;
        const res = await axios.post(`${API}/api/line-items/${item.id}/spend-entries`, {
          subteam: taskName,
          amount: spendUsd,
          note,
        });
        if (res.data) patchItem(item.id, res.data);
        toast.success(`+ $${Number(spendUsd).toLocaleString("en-US", { maximumFractionDigits: 0 })} ${taskName} spend recorded`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Spend not recorded — open Spend History to add it manually");
      }
    }
  }, [pendingEtaChange, updateStatus, patchItem]);

  // ── Material sourcing hub exits ──
  // "All sourced — Complete": everything the line needs came from stores.
  const handleSourcingComplete = useCallback(async () => {
    if (!pendingEtaChange) return;
    const { item, field } = pendingEtaChange;
    setPendingEtaChange(null);
    await updateStatus(item.id, field, "Complete", "");
    toast.success("Material marked Complete — sourced from stores");
  }, [pendingEtaChange, updateStatus]);

  // "More coming — Ordered": stores covered part of it; the rest is on order
  // (or still to be ordered). Preserves any existing ETA instead of clearing it.
  const handleSourcingOrdered = useCallback(async () => {
    if (!pendingEtaChange) return;
    const { item, field } = pendingEtaChange;
    setPendingEtaChange(null);
    await updateStatus(item.id, field, "Ordered", item.material_eta || "");
  }, [pendingEtaChange, updateStatus]);

  // Reopen the hub from the row's sourcing pill — same modal, no status intent.
  const handleOpenSourcing = useCallback((item) => {
    setPendingEtaChange({ item, field: "material_status", taskName: "Material", newValue: "Ordered" });
  }, []);

  const handleSpendClick = useCallback((item) => setSpendHistoryItem(item), []);
  const handleSpendHistoryItemUpdate = useCallback((updated) => {
    setSpendHistoryItem(prev => (prev && prev.id === updated.id ? updated : prev));
    // Reconcile the row in global state so the spend total + table reflect immediately.
    if (updated && updated.id) patchItem(updated.id, updated);
  }, [patchItem]);

  const handleMfgStatusChange = useCallback((itemId, val) => {
    if (val === "Third Party") {
      const it = items.find(i => i.id === itemId);
      setThirdPartyItem(it || { id: itemId });
      setThirdPartyWhere(it?.third_party_where || "");
      setThirdPartyEta(it?.third_party_eta || "");
    } else {
      updateStatus(itemId, "manufacturing_status", val);
    }
  }, [updateStatus, items]);

  const handleThirdPartyConfirm = useCallback(async () => {
    const itemId = thirdPartyItem.id;
    setThirdPartyItem(null);
    await updateStatus(itemId, "manufacturing_status", "Third Party");
    if (thirdPartyWhere || thirdPartyEta) {
      await editItem(itemId, { third_party_where: thirdPartyWhere, third_party_eta: thirdPartyEta });
    }
    setThirdPartyWhere(""); setThirdPartyEta("");
  }, [thirdPartyItem, thirdPartyWhere, thirdPartyEta, updateStatus, editItem]);

  const handleQcStatusChange = useCallback((itemId, val) => {
    if (val === "Rework Required") {
      // Find the item so we can hand it to the Send-Back modal directly.
      const item = items.find(i => i.id === itemId);
      if (item) {
        setReturnReasonItem(item);
        setReturnReason("");
        setReturnIsRework(true);
        setReturnDrawingLinks([""]);
        // Persist the new QC status alongside opening the modal so the
        // dropdown reflects the latest selection even if the user cancels.
        // This mirrors how Failed/Passed save instantly.
        updateStatus(itemId, "qc_status", val);
        return;
      }
    }
    updateStatus(itemId, "qc_status", val);
  }, [updateStatus, items]);

  // Toggle the Quality Dossier checkbox. Persists via editItem so the
  // server log picks up who flipped it, and global items state is
  // optimistically refreshed for instant UI feedback.
  const handleQdToggle = useCallback((itemId, next) => {
    editItem(itemId, { quality_dossier_ready: !!next });
  }, [editItem]);

  const handleCustomerAcceptanceToggle = useCallback((itemId, next) => {
    editItem(itemId, { customer_acceptance: !!next });
  }, [editItem]);

  const handleDispatchStatusChange = useCallback(async (item, newStatus) => {
    if (newStatus === "Shipped") {
      setDispatchShippedItem(item);
      setDispatchCarrier(item.carrier_name || "");
      setDispatchTrackingId(item.tracking_id || "");
      setDispatchShippedAt(new Date().toISOString().slice(0, 10));
    } else {
      await updateStatus(item.id, "dispatch_status", newStatus);
    }
  }, [updateStatus]);

  const handleDispatchShippedConfirm = useCallback(async () => {
    const item = dispatchShippedItem;
    setDispatchShippedItem(null);
    await updateStatus(item.id, "dispatch_status", "Shipped");
    const shippingData = {};
    if (dispatchCarrier) shippingData.carrier_name = dispatchCarrier;
    if (dispatchTrackingId) shippingData.tracking_id = dispatchTrackingId;
    if (dispatchShippedAt) shippingData.shipped_at = dispatchShippedAt;
    if (Object.keys(shippingData).length) {
      await editItem(item.id, shippingData);
    }
    setDispatchCarrier(""); setDispatchTrackingId(""); setDispatchShippedAt(new Date().toISOString().slice(0, 10));
  }, [dispatchShippedItem, dispatchCarrier, dispatchTrackingId, dispatchShippedAt, updateStatus, editItem]);

  // ── Step 6: Return reason validation — must be > 5 chars ──────────────────
  const returnReasonValid = returnReason.trim().length > 5;
  // Non-empty drawing links must all look like http(s) URLs
  const returnDrawingLinksCleaned = returnDrawingLinks.map(s => s.trim()).filter(Boolean);
  const returnDrawingLinksValid = returnDrawingLinksCleaned.every(
    s => /^https?:\/\/.+\..+/i.test(s)
  );

  const stageBg = "transparent";

  // ── Row props builder ──────────────────────────────────────────────────────
  const sharedRowProps = {
    stage,
    stageColor: sc.color,
    isLastStage,
    isFirstStage,
    canEdit,
    canDelete,
    showValue,
    showSpend,
    partDuplicateMap,
    navigate,
    onAdvanceClick: handleAdvanceClick,
    onReturnClick: handleOpenReturn,
    onReturnToCustomer: handleOpenReturnToCustomer,
    onBlockClick: handleOpenBlock,
    onUnblock: handleUnblock,
    onProcStatusChange: handleProcStatusChange,
    onOpenSourcing: handleOpenSourcing,
    onOpenBom: handleOpenBom,
    onBuildTypeChange: handleBuildTypeChange,
    canDefineBom,
    canBuyBom,
    onMfgStatusChange: handleMfgStatusChange,
    onSetRouter: setRouterCard,
    onSetKitting: setKittingComplete,
    onQcStatusChange: handleQcStatusChange,
    onQdToggle: handleQdToggle,
    onCustomerAcceptanceToggle: handleCustomerAcceptanceToggle,
    onDispatchStatusChange: handleDispatchStatusChange,
    onSpendClick: handleSpendClick,
    onNextActionClick: handleNextActionClick,
    onErpClick: handleErpChipClick,
    isAdmin,
    canErpEdit,
    densityCfg,
  };

  return (
    <div
      style={{ minHeight: "100%" }}
      data-testid={`stage-page-${stage.toLowerCase().replace(/\s/g, "-")}`}
    >
      {/* ── Unified stage card ── */}
      <div className="rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))]">
        {/* Stage accent line — swipes IN on stage change, from the side the
            pipeline chevron travelled from, timed to the chevron glide (~420ms).
            key={stage} remounts the inner bar so the fill replays each switch. */}
        <div className="h-[2px]">
          <div
            key={stage}
            className="h-full rl-stage-accent"
            style={{ backgroundColor: sc.color, transformOrigin: accentSwipeDir === "rtl" ? "right" : "left" }}
          />
        </div>
        <div className="space-y-0">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 px-5 pt-4 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <PageTitle
            color={sc.color}
            title={stage}
            count={totalItems}
            unit="lines"
            after={
              <button
                type="button"
                onClick={() => setSearchOpen(o => !o)}
                aria-label="Search"
                data-testid="stage-search-toggle"
                className={`md:hidden self-center p-1 -m-0.5 rounded transition-colors ${searchOpen ? "text-orange-500" : "text-[hsl(var(--muted-foreground))]"}`}
              >
                <Search className="w-4 h-4" />
              </button>
            }
          >
            <div className="flex flex-nowrap items-center gap-x-2 text-sm text-[hsl(var(--muted-foreground))] mt-0.5 min-h-[22px] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {stage === "Manufacturing" && reworkStageItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowReworkOnly(prev => !prev)}
                  className={`rework-lustre group/rework inline-flex items-center gap-1 pl-1.5 pr-1.5 h-5 rounded-full text-[10px] font-bold uppercase tracking-wider leading-none transition-all duration-150 ${
                    showReworkOnly ? "" : "rl-pill hover:pr-5"
                  }`}
                  style={showReworkOnly
                    ? { backgroundColor: "#14b8a6", color: "#fff", border: "1px solid #14b8a6", boxShadow: "inset 0 2px 4px rgba(0,0,0,0.25)" }
                    : { backgroundColor: "rgba(20,184,166,0.06)", color: "#0d9488", border: "1px solid rgba(20,184,166,0.40)" }
                  }
                  title={showReworkOnly ? "Show all manufacturing items" : `Click to filter — show only the ${reworkStageItems.length} rework item${reworkStageItems.length === 1 ? '' : 's'}`}
                  data-testid="rework-inline-badge"
                >
                  <ReworkIcon className="w-3 h-3 shrink-0" strokeWidth={2.4} />
                  <span className="font-mono-data normal-case">{reworkStageItems.length}</span>
                  <span className="normal-case">in rework</span>
                  {/* Hover-only filter cue (hidden when filter is already active) */}
                  {!showReworkOnly && (
                    <Filter
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 opacity-0 group-hover/rework:opacity-100 transition-opacity duration-150"
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  )}
                </button>
              )}
              {(searchQuery || filteredByGlobal) && stageItems.length !== totalItems && (
                <span className="text-xs">(<span className="font-mono-data">{stageItems.length}</span> shown)</span>
              )}
              {!canEdit && <span className="text-xs px-2 py-0.5 bg-[hsl(var(--muted))] rounded-full">Read-only</span>}
              {(() => {
                const labels = [];
                if (searchQuery.trim()) labels.push({ key: "search", label: `search "${searchQuery.trim().slice(0, 22)}${searchQuery.trim().length > 22 ? "…" : ""}"`, clear: () => setSearchQuery("") });
                if (globalFilter.clients) {
                  const _chips = resolveClientFilterChips(globalFilter.clients, clients, clientGroups);
                  const _label = _chips.length === 0
                    ? `clients: ${globalFilter.clients.length}`
                    : _chips.length <= 2
                      ? _chips.map(c => c.label).join(", ")
                      : `${_chips[0].label} +${_chips.length - 1} more`;
                  labels.push({ key: "client", label: _label });
                }
                if (globalFilter.priority !== "all") labels.push({ key: "priority", label: `priority: ${globalFilter.priority}` });
                if (globalFilter.flag === "blocked") labels.push({ key: "flag", label: "blocked only", clear: () => setGlobalFilter(f => ({ ...f, flag: null })) });
                else if (globalFilter.flag === "overdue") labels.push({ key: "flag", label: "overdue only", clear: () => setGlobalFilter(f => ({ ...f, flag: null })) });
                if (showDueThisWeekOnly) labels.push({ key: "due", label: `lines due in ${dueThisWeekDays}d`, clear: () => setShowDueThisWeekOnly(false) });
                if (showBlockedOnly) labels.push({ key: "blocked", label: "blocked only", clear: () => setShowBlockedOnly(false) });
                if (showReworkOnly) labels.push({ key: "rework", label: "rework only", clear: () => setShowReworkOnly(false) });
                if (showQcPendingOnly) labels.push({ key: "qc-pending", label: "pending QC only", clear: () => setShowQcPendingOnly(false) });
                if (procSubteamFilter) {
                  const teamLabel = procSubteamFilter.team.charAt(0).toUpperCase() + procSubteamFilter.team.slice(1);
                  labels.push({
                    key: "subteam",
                    label: `${teamLabel} · ${procSubteamFilter.status.toLowerCase()}`,
                    clear: () => setProcSubteamFilter(null)
                  });
                }
                if (mfgStatusFilter.length) labels.push({ key: "mfg-status", label: `mfg: ${mfgStatusFilter.map(s => s === "Queued" ? "Kit Done" : s).join(", ")}`, clear: () => setMfgStatusFilter([]) });
                if (routerPendingOnly) labels.push({ key: "router-pending", label: "route card pending", clear: () => setRouterPendingOnly(false) });
                if (kittingPendingOnly) labels.push({ key: "kitting-pending", label: "kitting pending", clear: () => setKittingPendingOnly(false) });
                if (qcStatusFilter.length) labels.push({ key: "qc-status", label: `qc: ${qcStatusFilter.join(", ")}`, clear: () => setQcStatusFilter([]) });
                if (qdPendingOnly) labels.push({ key: "qd-pending", label: "QD pending", clear: () => setQdPendingOnly(false) });
                if (caAcceptPendingOnly) labels.push({ key: "ca-pending", label: "CA pending", clear: () => setCaAcceptPendingOnly(false) });
                if (dispatchStatusFilter.length) labels.push({ key: "dispatch-status", label: `dispatch: ${dispatchStatusFilter.join(", ")}`, clear: () => setDispatchStatusFilter([]) });
                if (dueFilter) labels.push({ key: "due-filter", label: `due: ${summarizeDueFilter(dueFilter)}`, clear: () => setDueFilter(null) });
                if (shippedAtFilter) labels.push({ key: "shipped-at-filter", label: `shipped: ${summarizeDueFilter(shippedAtFilter)}`, clear: () => setShippedAtFilter(null) });
                if (labels.length === 0) return null;
                const localOnly = labels.every(l => typeof l.clear === "function");
                return (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-orange-500/8 border border-orange-500/25 text-[11px]"
                    data-testid="active-filters-chip"
                  >
                    <Filter className="w-3 h-3 text-orange-500 shrink-0" />
                    <span className="text-orange-500/90 font-semibold uppercase tracking-wider text-[9px]">Filtered by</span>
                    <span className="text-[hsl(var(--foreground))] truncate max-w-[420px]">
                      {labels.map(l => l.label).join(" · ")}
                    </span>
                    {localOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          labels.forEach(l => l.clear && l.clear());
                        }}
                        className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider text-orange-500 hover:text-orange-400 transition-colors"
                        data-testid="clear-active-filters-btn"
                        title="Clear all filters applied here"
                      >
                        <X className="w-2.5 h-2.5" /> Clear
                      </button>
                    )}
                  </span>
                );
              })()}
            </div>
          </PageTitle>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className={`group/search relative ${searchOpen ? "block" : "hidden"} md:block w-full md:w-auto`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] group-focus-within/search:text-orange-500 transition-colors" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search items, PO#, client..."
              className="h-8 pl-8 pr-8 text-xs rounded-full rl-search bg-[hsl(var(--muted))]/50 border border-transparent focus:bg-[hsl(var(--card))] focus:border-orange-500/50 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none w-full sm:w-64"
              aria-label="Search items"
              data-testid="stage-search"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded" aria-label="Clear search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Machine Monitor (Phase 0): glass "Shop floor view" toggle, opens the
              inline shop-floor drawer below the Status section. */}
          {stage === "Manufacturing" && (isAdmin || canDo("can_view_machines")) && (
            <button
              type="button"
              onClick={toggleShopFloor}
              aria-pressed={shopFloorOpen}
              disabled={shopFloorPhase === "opening" || shopFloorPhase === "closing"}
              data-testid="shop-floor-toggle"
              title={shopFloorOpen ? "Close the shop-floor view" : "Open the live shop-floor view"}
              className={`flex items-center gap-1.5 px-3.5 h-8 rounded-full text-[11px] font-semibold uppercase tracking-[0.05em] transition-all duration-150 ${
                shopFloorOpen
                  ? "rl-carved bg-[hsl(var(--muted))]/70 text-orange-600 dark:text-orange-400 border border-orange-500/40 translate-y-px"
                  : "rl-glass-edge bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]"
              }`}
            >
              {shopFloorPhase === "opening" || shopFloorPhase === "closing"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Factory className="w-3.5 h-3.5" />}
              {shopFloorPhase === "opening" ? "Opening…" : shopFloorPhase === "closing" ? "Closing…" : "Shop floor view"}
            </button>
          )}

          {/* Machine Care — the way INTO the Maintenance module. Deliberately
              louder than a filter chip and in the module's own steel blue, so
              production discovers it; it turns red and counts when machines
              are actually down, and stays quiet when they aren't. */}
          {showMaintenanceLink && (
            <button
              type="button"
              onClick={() => navigate("/maintenance")}
              data-testid="maintenance-module-link"
              title={machinesDown
                ? `${machinesDown} machine${machinesDown === 1 ? "" : "s"} with an open breakdown — open Maintenance`
                : "Report a machine down, or see how the fleet is running"}
              className={`group flex items-center gap-1.5 px-3.5 h-8 rounded-full text-[11px] font-semibold uppercase tracking-[0.05em] transition-all duration-150 rl-glass-edge ${
                machinesDown
                  ? "bg-red-500/8 text-red-600 dark:text-red-400 border border-red-500/40 hover:bg-red-500/12"
                  : "bg-[#2F6DAE]/8 text-[#2F6DAE] dark:text-[#7FB2E5] border border-[#2F6DAE]/35 hover:bg-[#2F6DAE]/14"
              }`}
            >
              <Wrench className="w-3.5 h-3.5" />
              Machine Care
              {machinesDown > 0 && (
                <span className="rl-carved ml-0.5 px-1.5 py-px rounded-full text-[10px] font-mono-data font-bold bg-red-500/15">
                  {machinesDown} down
                </span>
              )}
              <ArrowRight className="w-3 h-3 opacity-50 transition-transform duration-150 group-hover:translate-x-0.5" />
            </button>
          )}

          {canEdit && stage === "PO Received" && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-lit-btn bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors"
              data-testid="add-item-button"
            >
              <Plus className="w-3.5 h-3.5" /> Add Item
            </button>
          )}

          {canEdit && stage === "PO Received" && (isAdmin || canDo("can_import")) && (
            <button
              onClick={() => setShowBulkAddModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-500 hover:bg-orange-500/10 hover:border-orange-500/60 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors"
              data-testid="bulk-add-button"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Bulk Add
            </button>
          )}

          {/* Client requests: non-admins ask, admins approve (alert-styled, count-badged) */}
          {canEdit && stage === "PO Received" && !isAdmin && (
            <button
              onClick={() => setShowRequestClient(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-500 hover:bg-orange-500/10 hover:border-orange-500/60 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors"
              data-testid="request-client-button"
              title="Ask an admin to add a new client to the master list"
            >
              <UserPlus className="w-3.5 h-3.5" /> Request Client
            </button>
          )}
          {stage === "PO Received" && isAdmin && clientReqCount > 0 && (
            <button
              onClick={() => setShowApproveClients(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-pill border border-amber-500/50 bg-amber-500/5 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/70 text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors"
              data-testid="approve-clients-button"
              title="New client requests are waiting for your approval"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Approve new clients
              <span className="font-mono-data text-[10px] font-bold px-1.5 py-px rounded-full bg-amber-600 text-white leading-none">{clientReqCount}</span>
            </button>
          )}

          {/* Table density toggle */}
          <div className="hidden md:block relative">
            <div
              className="inline-flex items-center gap-0.5 rounded-full rl-carved bg-[hsl(var(--muted))]/60 p-[3px]"
              role="group"
              aria-label="Table density"
              data-testid="density-toggle"
            >
              {[
                { key: "compact",     icon: Rows3,              label: "Compact" },
                { key: "comfortable", icon: Rows,               label: "Comfortable" },
              ].map(({ key, icon: Icon, label }) => (
                <TooltipProvider key={key}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleDensityChange(key)}
                        aria-pressed={density === key}
                        aria-label={`${label} density`}
                        data-testid={`density-${key}-btn`}
                        className={`flex items-center justify-center h-6 w-8 rounded-full transition-colors ${
                          density === key
                            ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                            : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p>{label}</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
            {/* Inline density-switching indicator — sits ABOVE the toggle so
                it never clips off the right edge of the viewport. Monochrome
                to keep the chrome calm. */}
            {densitySwitching && (
              <div
                className="absolute bottom-full right-0 mb-1.5 flex items-center gap-1.5 whitespace-nowrap text-[hsl(var(--muted-foreground))] animate-in fade-in slide-in-from-bottom-1 duration-150 pointer-events-none"
                role="status"
                aria-live="polite"
                data-testid="density-switching-indicator"
              >
                <Loader2 className="w-3 h-3 animate-spin opacity-70" />
                <span className="text-[10px] uppercase tracking-widest font-semibold">
                  Applying {density}
                </span>
              </div>
            )}
            {densityHint && <DensityHintBubble kind={densityHint} onDismiss={dismissHint} />}
          </div>
        </div>
      </div>
      <div className="border-t border-[hsl(var(--border))]" />

      {/* ── Stats strip ──
          Top row: cross-cutting stats (Blocked / Overdue / Due Soon) — left
          aligned with a "STATS:" prefix. Compact pills, hover-reveal funnel.
          Bottom row: stage-specific status + gate counters — centered, with
          a label-over-number vertical stack tinted in the stage's color.
          Both rows share the same active/funnel vocabulary. */}
      {stage !== "Completed" && !(stage === "Manufacturing" && shopFloorOpen) && (() => {
        const globalEntries = [];
        const stageEntries = [];
        if (blockedStageItems.length > 0) {
          const pct = stageScopedItems.length > 0 ? blockedStageItems.length / stageScopedItems.length : 0;
          globalEntries.push({
            key: "blocked", label: "Blocked", count: blockedStageItems.length, color: "#ef4444",
            active: showBlockedOnly, onClick: () => setShowBlockedOnly(v => !v),
            highAlert: pct > 0.05,
          });
        }
        if (overdueCount > 0) {
          const isOn = dueFilter?.kind === "operator" && dueFilter.op === "overdue";
          globalEntries.push({
            key: "overdue", label: "Overdue", count: overdueCount, color: "#ea580c",
            active: isOn, onClick: () => setDueFilter(isOn ? null : { kind: "operator", op: "overdue" }),
          });
        }
        if (dueThisWeekItems.length > 0) {
          globalEntries.push({
            key: "due-soon", label: "Due Soon", count: dueThisWeekItems.length, color: sc.color,
            active: showDueThisWeekOnly, onClick: () => setShowDueThisWeekOnly(v => !v),
          });
        }
        const stageColor = sc.color;
        if (stage === "Manufacturing") {
          const routerPending = stageScopedItems.filter(
            (i) => i.mfg_gate_version === "v1" && !i.router_card_number
          ).length;
          const kittingPending = stageScopedItems.filter(
            (i) => i.mfg_gate_version === "v1" && !i.kitting_complete
          ).length;
          if (routerPending > 0 || routerPendingOnly) {
            stageEntries.push({
              key: "router-pending", label: "RC Pending", count: routerPending, color: stageColor,
              active: routerPendingOnly,
              onClick: () => {
                setRouterPendingOnly(v => !v);
                setKittingPendingOnly(false);
              },
            });
          }
          if (kittingPending > 0 || kittingPendingOnly) {
            stageEntries.push({
              key: "kitting-pending", label: "Kitting Pending", count: kittingPending, color: stageColor,
              active: kittingPendingOnly,
              onClick: () => {
                setKittingPendingOnly(v => !v);
                setRouterPendingOnly(false);
              },
            });
          }
          MANUFACTURING_STATUSES.forEach(s => {
            const isQueued = s === "Queued";
            const count = isQueued
              ? stageScopedItems.filter(i => (i.manufacturing_status === "Queued" || !i.manufacturing_status) && !!i.kitting_complete).length
              : statusCounts[s];
            if (!count) return;
            stageEntries.push({
              key: `mfg-${s}`,
              label: isQueued ? "Kit Done, Queued for Mfg" : s,
              displayLabel: isQueued
                ? <span className="whitespace-nowrap">Kit Done, Queued</span>
                : null,
              count,
              color: stageColor,
              active: mfgStatusFilter.includes(s),
              onClick: () => setMfgStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]),
            });
          });
        } else if (stage === "Quality Control") {
          QC_STATUSES.forEach(s => {
            if (!statusCounts[s]) return;
            stageEntries.push({
              key: `qc-${s}`, label: s, count: statusCounts[s], color: stageColor,
              active: qcStatusFilter.includes(s),
              onClick: () => setQcStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]),
            });
          });
          // Quality Dossier gate — items pre-cleared (`qc_status=Passed`) but
          // still waiting on dossier sign-off (`quality_dossier_ready=false`).
          // Surfaced as a clickable tile so the QC owner can spot the bottleneck.
          const qdPending = stageScopedItems.filter(
            (i) => i.qc_status === "Passed" && !i.quality_dossier_ready
          ).length;
          if (qdPending > 0 || qdPendingOnly) {
            stageEntries.push({
              key: "qd-pending", label: "QD Pending", count: qdPending, color: stageColor,
              active: qdPendingOnly,
              onClick: () => setQdPendingOnly(v => !v),
            });
          }
          // Customer Acceptance gate — QD Ready but awaiting customer sign-off.
          const caPending = stageScopedItems.filter(
            (i) => i.quality_dossier_ready && !i.customer_acceptance
          ).length;
          if (caPending > 0 || caAcceptPendingOnly) {
            stageEntries.push({
              key: "ca-pending", label: "CA Pending", count: caPending, color: "#0ea5e9",
              active: caAcceptPendingOnly,
              onClick: () => setCaAcceptPendingOnly(v => !v),
            });
          }
        } else if (stage === "Dispatch") {
          DISPATCH_STATUSES.forEach(s => {
            if (!statusCounts[s]) return;
            stageEntries.push({
              key: `disp-${s}`, label: s, count: statusCounts[s], color: stageColor,
              active: dispatchStatusFilter.includes(s),
              onClick: () => setDispatchStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]),
            });
          });
        }
        if (globalEntries.length === 0 && stageEntries.length === 0 && stage !== "Dispatch" && stage !== "Quality Control") return null;
        const showTile = stage === "Dispatch" || stage === "Quality Control";
        const hasEntries = globalEntries.length > 0 || stageEntries.length > 0;
        return (
          <div className="flex items-stretch gap-4 px-5 py-3" data-testid="stage-stats-strip">
            {hasEntries && (
              <div className="flex-1 min-w-0 space-y-3">
                {globalEntries.length > 0 && (
                  <div className="flex items-center gap-x-2 gap-y-2 flex-nowrap overflow-x-auto text-[11px] md:flex-wrap md:gap-x-2 md:overflow-visible">
                    <span className="hidden md:inline-block md:w-24 shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none" aria-hidden="true">
                      Attention
                    </span>
                    {globalEntries.map((e) => (
                      <AttentionChip key={e.key} kind={e.key} count={e.count} active={e.active} severe={!!e.highAlert} trailing="filter" size="sm" onClick={e.onClick} testId={`stat-${e.key}`} />
                    ))}
                  </div>
                )}
                {globalEntries.length > 0 && stageEntries.length > 0 && (
                  <div className="hidden md:block border-t border-[hsl(var(--border))]/50" aria-hidden="true" />
                )}
                {stageEntries.length > 0 && (
                  <div className="flex items-center gap-x-2 gap-y-2 overflow-x-auto px-1 -mx-1 pb-1 md:flex-wrap md:gap-x-2 md:justify-start md:overflow-visible md:px-0 md:mx-0 md:pb-0" data-testid="stage-stats-grouped">
                  <span className="hidden md:inline-block md:w-24 shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none" aria-hidden="true">
                    Status
                  </span>
                  {stageEntries.map((e) => {
                      const raw = e.key.replace(/^(mfg|qc|disp)-/, "");
                      let iconNode;
                      if (e.key.startsWith("mfg-")) {
                        const st = raw === "Machining Complete" ? "complete" : raw === "Processing" ? "processing" : raw === "Third Party" ? "third-party" : "queued";
                        iconNode = <CncMachineIcon size={15} state={st} />;
                      } else if (e.key.startsWith("qc-") || e.key.startsWith("disp-")) {
                        iconNode = <StatusGlyph status={raw} size={15} />;
                      } else if (e.key === "router-pending") {
                        iconNode = <RouterCardIcon size={15} state="pending" />;
                      } else if (e.key === "kitting-pending") {
                        iconNode = <KittingTrayIcon size={15} state="pending" />;
                      } else if (e.key === "qd-pending") {
                        iconNode = <ClipboardCheck style={{ width: 15, height: 15, color: e.color }} />;
                      } else if (e.key === "ca-pending") {
                        iconNode = <UserCheck style={{ width: 15, height: 15, color: e.color }} />;
                      } else {
                        iconNode = <Circle style={{ width: 15, height: 15, color: e.color }} />;
                      }
                      return (
                        <AttentionChip
                          key={e.key}
                          label={e.displayLabel || e.label}
                          count={e.count}
                          color={e.color}
                          iconNode={iconNode}
                          active={e.active}
                          trailing="filter"
                          size="sm"
                          onClick={e.onClick}
                          testId={`stat-${e.key}`}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {showTile && stage === "Dispatch" && (
              <StageVelocityTile
                velocity={dispatchVelocity}
                title="Shipped + Delivered"
                subtitle="items with a shipped on date"
                tooltip={<>Items with a <strong>Shipped On</strong> date in the period — in Dispatch stage (status Shipped or Delivered) or in the Completed stage.</>}
              />
            )}
            {showTile && stage === "Quality Control" && (
              <StageVelocityTile
                velocity={qcVelocity}
                title="Lines done"
                subtitle="items that completed QC"
                tooltip={<>Items that <strong>cleared QC</strong> and entered Dispatch in the period, based on the stage transition timestamp.</>}
              />
            )}
          </div>
        );
      })()}

      {stage === "Procurement" && procPending && <div className="border-t border-[hsl(var(--border))]" />}
      {/* ── Procurement Sub-team Status Bar ── */}
      {stage === "Procurement" && procPending && (
        <div
          className="px-5 py-3 relative"
          data-testid="procurement-status-bar"
        >
          <div className="flex items-center justify-between mb-2 min-h-[1.75rem]">
            <span className="text-[10px] uppercase font-bold tracking-widest text-[hsl(var(--muted-foreground))]">
              Sub-teams
            </span>
            {procSubteamFilter && (
              <button
                onClick={() => setProcSubteamFilter(null)}
                className="flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors px-2 py-1 rounded-md hover:bg-[hsl(var(--muted))]"
                data-testid="clear-proc-filter"
              >
                <X className="w-3 h-3" />Clear filter
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5 lg:gap-7 items-stretch">
            {/* LEFT: bar rows */}
            <div>
              {[
                { label: "Material", key: "material", counts: procPending.material, tc: PROC_TEAM_COLORS.Material, Icon: Package },
                { label: "Tooling",  key: "tooling",  counts: procPending.tooling,  tc: PROC_TEAM_COLORS.Tooling,  Icon: Wrench  },
                { label: "Gauging",  key: "gauging",  counts: procPending.gauging,  tc: PROC_TEAM_COLORS.Gauging,  Icon: Ruler  },
              ].filter(({ label }) =>
                // Division lens (Phase 1b): a scoped lens hides sub-teams the
                // division doesn't use (drilling: no Gauging). All lens keeps
                // all three (mixed divisions on the page).
                divisionLens === "all" ||
                (divArea(divisionConfig, divisionLens, "procurement").subteams || ["Material", "Tooling", "Gauging"]).includes(label)
              ).map(({ label, key, counts, tc, Icon }, idx, arr) => {
                const total = counts.pending + counts.ordered + counts.complete;
                const pct = (n) => total > 0 ? `${(n / total) * 100}%` : "0%";
                const isPendingActive  = procSubteamFilter?.team === key && procSubteamFilter?.status === "Pending";
                const isOrderedActive  = procSubteamFilter?.team === key && procSubteamFilter?.status === "Ordered";
                const isCompleteActive = procSubteamFilter?.team === key && procSubteamFilter?.status === "Complete";
                const anyActive = !!procSubteamFilter;

                const toggle = (status) =>
                  setProcSubteamFilter(p =>
                    p && p.team === key && p.status === status ? null : { team: key, status }
                  );

                return (
                  <div key={key} className={`grid grid-cols-[180px_1fr] gap-x-4 md:gap-x-5 items-center${idx < arr.length - 1 ? " mb-3" : ""}`}>
                    {/* TEAM BUTTON — Pending filter. Wears the AttentionChip
                        vocabulary: .rl-tile lifted card, .rl-pressed + tinted
                        inset when active, .rl-ico-well icon bubble. */}
                    <button
                      type="button"
                      onClick={() => toggle("Pending")}
                      className={`group flex items-center gap-2.5 w-full text-left rounded-[10px] border px-2.5 py-1.5 rl-tile ${isPendingActive ? "rl-pressed" : ""} transition-all focus:outline-none focus-visible:ring-2`}
                      style={{
                        background: isPendingActive ? `${tc.color}12` : "hsl(var(--card))",
                        borderColor: isPendingActive ? `${tc.color}59` : "hsl(var(--border))",
                        boxShadow: isPendingActive ? `inset 0 2px 5px ${tc.color}30` : undefined,
                        opacity: anyActive && !isPendingActive ? 0.45 : 1,
                      }}
                      data-testid={`proc-filter-${key}`}
                      title={isPendingActive ? `Remove ${label} pending filter` : `Filter to ${counts.pending} items pending ${label}`}
                    >
                      <span className="flex items-center justify-center w-[30px] h-[30px] rounded-full rl-ico-well shrink-0" style={{ background: isPendingActive ? `${tc.color}29` : `${tc.color}1f` }}>
                        <Icon className="w-[15px] h-[15px]" style={{ color: tc.color }} aria-hidden="true" />
                      </span>
                      <span className="flex flex-col leading-tight min-w-0">
                        <span className="text-[12.5px] font-bold truncate" style={{ color: isPendingActive ? tc.color : "hsl(var(--foreground))" }}>{label}</span>
                        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]">
                          <span className="font-mono-data font-bold">{counts.pending}</span> pending
                        </span>
                      </span>
                      <Filter className={`w-3 h-3 shrink-0 ml-auto transition-opacity duration-150 ${isPendingActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} style={{ color: tc.color, fill: isPendingActive ? tc.color : "transparent" }} aria-hidden="true" />
                    </button>

                    {/* BAR + LEGEND — one line, so the bar's centre matches the
                        team tile's centre (legend used to hang below and push
                        the bar high against the tile). */}
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="flex flex-1 min-w-0 h-3 rounded-full overflow-hidden rl-track bg-[hsl(var(--muted))]/50" role="progressbar" aria-label={`${label} progress`} aria-valuenow={total > 0 ? Math.round((counts.complete / total) * 100) : 0} aria-valuemin={0} aria-valuemax={100} data-testid={`proc-progress-${key}`}>
                        {counts.complete > 0 && (
                          <button type="button" onClick={() => toggle("Complete")} title={`${counts.complete} complete`} data-testid={`proc-progress-${key}-complete`}
                            className="h-full transition-all focus:outline-none"
                            style={{ width: pct(counts.complete), background: tc.color, opacity: anyActive && !isCompleteActive ? 0.4 : 1, boxShadow: isCompleteActive ? "inset 0 0 0 2px rgba(255,255,255,0.9)" : "inset 0 1px 0 rgba(255,255,255,0.30)" }} />
                        )}
                        {counts.ordered > 0 && (
                          <button type="button" onClick={() => toggle("Ordered")} title={`${counts.ordered} ordered`} data-testid={`proc-progress-${key}-ordered`}
                            className="h-full transition-all focus:outline-none"
                            style={{ width: pct(counts.ordered), background: `${tc.color}52`, opacity: anyActive && !isOrderedActive ? 0.4 : 1, boxShadow: isOrderedActive ? "inset 0 0 0 2px rgba(255,255,255,0.9)" : "none" }} />
                        )}
                        {counts.pending > 0 && (
                          <button type="button" onClick={() => toggle("Pending")} title={`${counts.pending} pending`} data-testid={`proc-progress-${key}-pending`}
                            className="h-full transition-all focus:outline-none"
                            style={{ width: pct(counts.pending), background: `${tc.color}1f`, opacity: anyActive && !isPendingActive ? 0.4 : 1, boxShadow: isPendingActive ? `inset 0 0 0 2px ${tc.color}` : "none" }} />
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button type="button" onClick={() => toggle("Complete")} data-testid={`proc-counts-${key}-complete`}
                          className="group inline-flex items-center text-[11px] px-2.5 py-1 rounded-md border transition-all focus:outline-none focus-visible:ring-2"
                          style={{ background: isCompleteActive ? tc.color : "transparent", borderColor: isCompleteActive ? tc.color : "transparent", opacity: anyActive && !isCompleteActive ? 0.45 : 1 }}
                          onMouseEnter={(e) => { if (!isCompleteActive) e.currentTarget.style.background = `${tc.color}14`; }}
                          onMouseLeave={(e) => { if (!isCompleteActive) e.currentTarget.style.background = "transparent"; }}>
                          <span className="w-[8px] h-[8px] rounded-full shrink-0 mr-1.5" style={{ background: isCompleteActive ? "#fff" : tc.color }} />
                          <span className="font-mono-data font-extrabold" style={{ color: isCompleteActive ? "#fff" : tc.color }}>{counts.complete}</span>
                          <span className="ml-1" style={{ color: isCompleteActive ? "rgba(255,255,255,0.85)" : "hsl(var(--muted-foreground))" }}>complete</span>
                          <Filter className={`w-3 h-3 ml-1.5 transition-opacity duration-150 ${isCompleteActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} style={{ color: isCompleteActive ? "#fff" : tc.color }} aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => toggle("Ordered")} data-testid={`proc-counts-${key}-ordered`}
                          className="group inline-flex items-center text-[11px] px-2.5 py-1 rounded-md border transition-all focus:outline-none focus-visible:ring-2"
                          style={{ background: isOrderedActive ? tc.color : "transparent", borderColor: isOrderedActive ? tc.color : "transparent", opacity: anyActive && !isOrderedActive ? 0.45 : 1 }}
                          onMouseEnter={(e) => { if (!isOrderedActive) e.currentTarget.style.background = `${tc.color}14`; }}
                          onMouseLeave={(e) => { if (!isOrderedActive) e.currentTarget.style.background = "transparent"; }}>
                          <span className="w-[8px] h-[8px] rounded-full shrink-0 mr-1.5" style={{ background: isOrderedActive ? "rgba(255,255,255,0.65)" : `${tc.color}52` }} />
                          <span className="font-mono-data font-extrabold" style={{ color: isOrderedActive ? "#fff" : tc.color }}>{counts.ordered}</span>
                          <span className="ml-1" style={{ color: isOrderedActive ? "rgba(255,255,255,0.85)" : "hsl(var(--muted-foreground))" }}>ordered</span>
                          <Filter className={`w-3 h-3 ml-1.5 transition-opacity duration-150 ${isOrderedActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`} style={{ color: isOrderedActive ? "#fff" : tc.color }} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT: velocity / ready tile */}
            <div className="lg:border-l lg:border-[hsl(var(--border))]/40 lg:pl-6">
              {readyCount > 0 ? (
                <div className="flex flex-col justify-center gap-2.5 h-full animate-in fade-in slide-in-from-right-1 duration-300">
                  <div className="flex items-center gap-2">
                    <span
                      className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full"
                      style={{ background: "rgba(168,85,247,0.12)", color: "#a855f7" }}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] leading-tight text-[hsl(var(--foreground))]">
                        <span className="font-mono-data font-bold" style={{ color: "#a855f7" }}>
                          {readyCount}
                        </span>{" "}
                        {readyCount === 1 ? "item" : "items"} fully procured
                      </div>
                      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                        ready to advance to Manufacturing
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setReadyForMfgOpen(true)}
                    className="flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full rl-lit-btn text-[12px] font-semibold text-white transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-purple-500"
                    style={{ backgroundColor: "#a855f7" }}
                    data-testid="ready-for-mfg-check-btn"
                  >
                    Review <ArrowRight className="w-3 h-3" />
                  </button>
                  <div className="text-[10px] text-center text-[hsl(var(--muted-foreground))] font-mono-data tabular-nums">
                    <span className="font-semibold">{velocity.today}</span> today
                    <span className="mx-1.5 opacity-50">·</span>
                    <span className="font-semibold">{velocity.week}</span> this week
                    <span className="mx-1.5 opacity-50">·</span>
                    <span className="font-semibold">{velocity.month}</span> this month
                  </div>
                </div>
              ) : (
                <div className="flex flex-col justify-center h-full">
                  <div className="flex items-center gap-1.5 mb-3">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#a855f7]" />
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[hsl(var(--muted-foreground))]">
                      Procurement Velocity
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { n: velocity.today, label: "Today" },
                      { n: velocity.week,  label: "This week" },
                      { n: velocity.month, label: "This month" },
                    ].map(({ n, label }) => (
                      <div key={label}>
                        <div className="font-mono-data text-lg font-semibold text-[hsl(var(--foreground))] leading-tight tabular-nums">
                          {n}
                        </div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2.5">
                    items advanced to manufacturing
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Desktop delete confirmation bar ── */}
      {confirmDeleteId && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border-y border-red-500/30 text-sm" data-testid="delete-confirm-bar">
          <span className="text-red-500 font-medium">Delete this item permanently? This cannot be undone.</span>
          <div className="flex items-center gap-2">
            <button onClick={handleDeleteCancel} className="px-3 py-1.5 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] text-xs font-medium hover:bg-[hsl(var(--muted))]/80 transition-colors" data-testid="delete-cancel-btn">Cancel</button>
            <button onClick={handleDeleteConfirm} className="px-3 py-1.5 rounded-md bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors" data-testid="delete-confirm-btn">Delete</button>
          </div>
        </div>
      )}
      <div className="border-t border-[hsl(var(--border))]" />

      {/* ── Machine Monitor (Phase 0): inline shop-floor drawer ──
          Splits the Manufacturing card open below the Status section to reveal
          the live top-view of the shop floor, in the same fixed-width container.
          The drawer mounts only when open and animates with a GPU-composited
          opacity + translateY (no max-height reflow), while the line-items table
          below is unmounted — so the open is smooth even with 100+ lines. */}
      {stage === "Manufacturing" && shopFloorOpen && (
        <div
          data-testid="shop-floor-drawer"
          className="relative px-5 py-4 bg-[hsl(var(--muted))]/20 border-b border-[hsl(var(--border))]"
        >
          {/* The floor mounts immediately (during "opening") behind the curtain,
              so its first paint is hidden; on "open" we fade the floor in and the
              curtain out for a smooth reveal. It stays visible during "closing" so
              the curtain (not a blank gap) is what the user sees on the way out. */}
          <div className={shopFloorPhase === "opening" ? "invisible" : "shop-floor-reveal"}>
            <MachineFloor />
          </div>
          {(shopFloorPhase === "opening" || shopFloorPhase === "closing") && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-[hsl(var(--card))] shop-floor-curtain"
              data-testid="shop-floor-curtain"
            >
              <span className="inline-flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                <Loader2 className="w-4 h-4 animate-spin" />
                {shopFloorPhase === "closing" ? "Closing shop floor…" : "Loading shop floor…"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Table ── (unmounted while the shop-floor drawer is open) */}
      {!(stage === "Manufacturing" && shopFloorOpen) && (
      <>
      {/* ── Table ── */}
      {isInitialLoad ? (
        <TableSkeleton
          stage={stage}
          showValue={showValue}
          rows={Math.min(Math.max(totalItems, 5), 12)}
          stageColor={sc.color}
        />
      ) : displayItems.length === 0 && !isPending && !hasActiveFilters ? (
        <div className="flex flex-col items-center justify-center py-14 px-5 text-[hsl(var(--muted-foreground))]" data-testid="empty-stage">
          <Package className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">No items in this stage</p>
        </div>
      ) : (
        <div
          className="relative transition-opacity duration-150"
          style={{ opacity: (holding || isPending) ? 0.45 : 1 }}
          data-testid="stage-table"
        >
          {/* Mobile card list (< md) — MOUNT only below md (perf: avoid the
              double-mount of ~100 invisible rows per stage switch). The md:hidden
              class stays as belt-and-braces. */}
          {!isDesktop && (
          <div className="md:hidden" data-testid="stage-mobile-cards">
            {paginatedItems.length === 0 ? (
              <div className="py-10 text-center text-[hsl(var(--muted-foreground))]">
                <Package className="w-7 h-7 mx-auto mb-1.5 opacity-20" />
                <p className="text-xs font-medium">{hasActiveFilters ? "No items match the current filters" : "No items in this stage"}</p>
              </div>
            ) : paginatedItems.map(item => (
              <StageRowMobile
                key={item.id}
                {...sharedRowProps}
                item={item}
                clientColor={clientColorMap[item.client] || "#94a3b8"}
                clientLogoSvg={clientLogoMap[item.client] || ""}
                isAnimating={advancingId === item.id}
                isConfirmingDelete={confirmDeleteId === item.id}
                erpHighlight={erpHighlightId === item.id}
                advanceable={canAdvance(item)}
                onDeleteClick={handleDeleteClick}
                onDeleteConfirmInline={handleDeleteConfirmInline}
                onDeleteCancel={handleDeleteCancel}
                onDetailsClick={handleDetailsClick}
                onNextActionClick={handleNextActionClick}
              />
            ))}
          </div>
          )}

          {/* Desktop table (≥ md) — MOUNT only at md+ (the other half of the
              double-mount fix). The hidden md:block class stays as belt-and-braces. */}
          {isDesktop && (
          <div className="hidden md:block overflow-x-auto">
            <table className={`w-full text-sm rl-table density-${density}`}>
              <thead>
                <tr className="rl-thead border-t border-b border-[hsl(var(--border))]">
                  <th className={`text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))] ${densityCfg.descTh}`}>Item Description</th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  {/* Header key chips MIRROR the data cell below: same carved
                      (recessed) treatment, same 13px font, and the th uses the
                      row's px-3 so the Part # chip shares the row's left edge. */}
                  <span className="font-mono-data text-[13px] px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/40 font-semibold text-[hsl(var(--foreground))]">Part #</span>
                  <span className="font-mono-data text-[13px] font-normal px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]">Rev</span>
                  {/* Legend key for the row "also in" marker — wears the SAME
                      rl-also-chip skin as the row button (raised, not carved)
                      so the key visually matches what it explains; is-legend
                      disables the pressable affordance. */}
                  <span className="rl-also-chip is-legend self-stretch normal-case tracking-normal">
                    <Route className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
                    <span className="text-[11px] font-medium leading-none text-[hsl(var(--muted-foreground))]">also in</span>
                  </span>
                </div>
              </th>
                  <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Qty</th>
                  {(showValue || (stage === "Procurement" && showSpend)) && (
                    <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
                      {showValue ? "Value" : "Procurement Spend"}
                    </th>
                  )}
                  <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                    <div className="inline-flex items-center justify-center">
                      <DueFilterPopover
                        columnLabel={stage === "Completed" ? "Due Date" : "Due"}
                        value={dueFilter}
                        onChange={setDueFilter}
                        sort={dueSort}
                        onSortChange={setDueSort}
                        accentColor={STAGE_COLORS[stage]?.color || "#f97316"}
                        testIdPrefix="due-filter"
                        items={items}
                      />
                    </div>
                  </th>
                  {stage === "Completed" && (
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                      <div className="inline-flex items-center justify-center">
                        <DueFilterPopover
                          columnLabel="Shipped On"
                          value={shippedAtFilter}
                          onChange={setShippedAtFilter}
                          sort={shippedAtSort}
                          onSortChange={setShippedAtSort}
                          accentColor={STAGE_COLORS[stage]?.color || "#22c55e"}
                          testIdPrefix="shipped-at-filter"
                          items={items}
                        />
                      </div>
                    </th>
                  )}
                  {stage === "Procurement" && (
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Material · Tooling · Gauging</th>
                  )}
                  {stage === "Manufacturing" && (
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
                      <StatusFilterPopover
                        columnLabel="Mfg Status"
                        tooltip="Filter by manufacturing status"
                        statuses={MANUFACTURING_STATUSES}
                        selected={mfgStatusFilter}
                        onChange={setMfgStatusFilter}
                        accentColor={STAGE_COLORS.Manufacturing.color}
                        testIdPrefix="mfg-status-filter"
                        metaFilters={[
                          {
                            key: "router-pending",
                            label: "Route Card Pending",
                            active: routerPendingOnly,
                            onToggle: () => {
                              setRouterPendingOnly(v => !v);
                              setKittingPendingOnly(false);
                            },
                            hint: "Items still missing a Route Card release",
                          },
                          {
                            key: "kitting-pending",
                            label: "Kitting Pending",
                            active: kittingPendingOnly,
                            onToggle: () => {
                              setKittingPendingOnly(v => !v);
                              setRouterPendingOnly(false);
                            },
                            hint: "Items still missing a Kitting confirmation",
                          },
                        ]}
                      />
                    </th>
                  )}
                  {stage === "Quality Control" && (
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
                      <StatusFilterPopover
                        columnLabel="QC Status"
                        tooltip="Filter by QC status"
                        statuses={QC_STATUSES}
                        selected={qcStatusFilter}
                        onChange={setQcStatusFilter}
                        accentColor={STAGE_COLORS["Quality Control"].color}
                        testIdPrefix="qc-status-filter"
                        metaFilters={[
                          {
                            key: "qd-pending",
                            label: "Quality Dossier Pending",
                            active: qdPendingOnly,
                            onToggle: () => setQdPendingOnly(v => !v),
                            hint: "Items pre-cleared (Passed) but still awaiting dossier sign-off",
                          },
                          {
                            key: "ca-pending",
                            label: "Customer Acceptance Pending",
                            active: caAcceptPendingOnly,
                            onToggle: () => setCaAcceptPendingOnly(v => !v),
                            hint: "QD Ready but awaiting customer acceptance sign-off",
                          },
                        ]}
                      />
                    </th>
                  )}
                  {stage === "Dispatch" && (
                    <th className="text-center px-2 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                      <div className="inline-flex items-center justify-center">
                        <DueFilterPopover
                          columnLabel="Shipped On"
                          value={shippedAtFilter}
                          onChange={setShippedAtFilter}
                          sort={shippedAtSort}
                          onSortChange={setShippedAtSort}
                          accentColor={STAGE_COLORS.Dispatch.color}
                          testIdPrefix="shipped-at-filter"
                          items={items}
                        />
                      </div>
                    </th>
                  )}
                  {stage === "Dispatch" && (
                    <th className="text-left px-2 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
                      <StatusFilterPopover
                        columnLabel="Dispatch Status"
                        tooltip="Filter by dispatch status"
                        statuses={DISPATCH_STATUSES}
                        selected={dispatchStatusFilter}
                        onChange={setDispatchStatusFilter}
                        accentColor={STAGE_COLORS.Dispatch.color}
                        testIdPrefix="dispatch-status-filter"
                      />
                    </th>
                  )}
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.length === 0 ? (
                  <tr data-testid="stage-empty-row">
                    <td colSpan={99} className="px-4 py-10 text-center text-[hsl(var(--muted-foreground))]">
                      <Package className="w-7 h-7 mx-auto mb-1.5 opacity-20" />
                      <p className="text-xs font-medium">No items match the current filters</p>
                    </td>
                  </tr>
                ) : paginatedItems.map(item => (
                  <StageRowDesktop
                    key={item.id}
                    {...sharedRowProps}
                    item={item}
                    clientColor={clientColorMap[item.client] || "#94a3b8"}
                    clientLogoSvg={clientLogoMap[item.client] || ""}
                    isAnimating={advancingId === item.id}
                    isConfirmingDelete={confirmDeleteId === item.id}
                    erpHighlight={erpHighlightId === item.id}
                    advanceable={canAdvance(item)}
                    lockReason={getAdvanceLockReason(item)}
                    onEditClick={handleOpenEdit}
                    onDeleteClick={handleDeleteClick}
                    onDetailsClick={handleDetailsClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}


      {/* ── Pagination ── */}
      {!isInitialLoad && displayItems.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-5 py-2.5 border-t border-[hsl(var(--border))]">
          <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0">
            Showing <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{showingFrom}–{showingTo}</span> of <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{displayItems.length}</span> items
          </span>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Export this stage view — exports the FULL filtered set
                (across all pages) including stage-specific extras like
                spend + ETAs for Procurement. Distinct teal accent so it
                doesn't blend with pagination/page-size controls. */}
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => {
                      const { rowCount, filename } = exportStageView(stage, displayItems, { showValue });
                      toast.success(`Exported ${rowCount} ${stage} ${rowCount === 1 ? "line" : "lines"}`, { description: filename });
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border border-teal-500/30 bg-teal-500/6 text-teal-700 dark:text-teal-300 hover:bg-teal-500/15 hover:border-teal-500/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40"
                    data-testid="export-stage-view-btn"
                  >
                    <Sheet className="w-3.5 h-3.5" />
                    Export this view
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-[11px]">Download all {displayItems.length} filtered {stage} {displayItems.length === 1 ? "line" : "lines"} as a spreadsheet (CSV)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
              <span>Per page:</span>
              <div className="flex rounded-md overflow-hidden border border-[hsl(var(--border))]">
                {PAGE_SIZE_OPTIONS.map(size => (
                  <button key={size} onClick={() => { setItemsPerPage(size); setCurrentPage(1); }}
                    className={`px-2 py-0.5 text-[10px] font-mono-data transition-colors ${itemsPerPage === size ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold" : "bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                    data-testid={`page-size-${size}`}
                  >{size}</button>
                ))}
                <button onClick={() => { setItemsPerPage(0); setCurrentPage(1); }}
                  className={`px-2 py-0.5 text-[10px] transition-colors ${itemsPerPage === 0 ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold" : "bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                  data-testid="page-size-all"
                >All</button>
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                  className="px-1.5 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-mono-data"
                  data-testid="page-first">«</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-2 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="page-prev">‹ Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-xs text-[hsl(var(--muted-foreground))]">…</span>
                  ) : (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      className={`w-6 h-6 text-[10px] rounded border transition-colors font-mono-data ${currentPage === p ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))] font-semibold" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                      data-testid={`page-${p}`}
                    >{p}</button>
                  ))}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-2 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="page-next">Next ›</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                  className="px-1.5 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-mono-data"
                  data-testid="page-last">»</button>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}{/* end table unmount-while-drawer-open guard */}
        </div>{/* end space-y-0 */}
      </div>{/* end unified card */}

      {/* ── Modals ── */}
      {blocksV2 ? (
        <BlockModalV2
          item={blockModalItem ? (items.find(i => i.id === blockModalItem.id) || blockModalItem) : null}
          partySuggestions={partySuggestions}
          onClose={() => setBlockModalItem(null)}
          onAdd={async (payload) => { if (blockModalItem) { await addBlock(blockModalItem.id, payload); setBlockModalItem(null); } }}
          onClear={async (blockId, note) => { if (blockModalItem) await clearBlock(blockModalItem.id, blockId, note); }}
        />
      ) : (
        <BlockModal
          item={blockModalItem}
          onClose={() => setBlockModalItem(null)}
          onBlock={(payload) => { if (blockModalItem) { blockItem(blockModalItem.id, payload); setBlockModalItem(null); } }}
        />
      )}
      {showAddModal && <AddItemModal onClose={() => setShowAddModal(false)} onCreate={(data) => { createItem(data); setShowAddModal(false); }} />}
      {showBulkAddModal && <BulkAddModal onClose={() => setShowBulkAddModal(false)} />}
      {editModalItem && <EditItemModal item={editModalItem} showValue={showValue} onClose={() => setEditModalItem(null)} onSave={(data) => editItem(editModalItem.id, data)} />}

      {/* Client request / approval lightboxes (PO Received) */}
      {showRequestClient && <RequestClientModal onClose={() => setShowRequestClient(false)} />}
      {showApproveClients && (
        <ApproveClientsModal
          onClose={() => setShowApproveClients(false)}
          onDone={refreshClientReqCount}
        />
      )}

      {/* Per-item details + history lightbox */}
      <LineItemDetailsModal
        open={!!detailsItem}
        item={detailsItem}
        onClose={() => setDetailsItem(null)}
        canEdit={canEdit}
        canDelete={canDelete}
        onEditClick={canEditItemDetails ? handleOpenEdit : null}
        onDeleteClick={canDelete ? handleDeleteClick : null}
        allItems={items}
        onSplitSuccess={() => refreshData()}
      />

      {/* Blocked-item next-action capture */}
      <NextActionModal
        open={!!nextActionItem}
        item={nextActionItem}
        onClose={() => setNextActionItem(null)}
        onSaved={(updated) => { if (updated?.id) patchItem(updated.id, updated); }}
      />

      {/* PO Received: QMS Contract Review */}
      <Dialog open={!!mfgAdvanceItem && !!mfgAdvanceItem._isAccept} onOpenChange={(open) => { if (!open) setMfgAdvanceItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="qms-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[hsl(var(--foreground))]">
              <ClipboardCheck className="w-5 h-5 text-blue-500" /> QMS Contract Review
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-[hsl(var(--muted-foreground))]">
              <p className="text-sm leading-relaxed">Before accepting this order, confirm that the <strong className="text-[hsl(var(--foreground))]">Contract Review</strong> has been completed per <strong className="text-[hsl(var(--foreground))]">Rockpecker QMS</strong>.</p>
              {mfgAdvanceItem && (
                <div className="mt-3 px-3 py-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-0.5">Order</p>
                  <p className="text-sm font-semibold">{mfgAdvanceItem.item_description}</p>
                  <p className="font-mono-data text-xs font-semibold text-[hsl(var(--foreground))]/80"><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{mfgAdvanceItem.po_number} · {mfgAdvanceItem.client}</p>
                </div>
              )}
              <p className="text-sm font-semibold mt-3">Has the Contract Review been completed?</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button onClick={() => setMfgAdvanceItem(null)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="qms-no-btn">No</button>
            <button onClick={handleAdvanceDocsConfirm} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="qms-yes-btn">Yes — Accept Order</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manufacturing: Advance Confirmation */}
      <Dialog open={!!mfgAdvanceItem && !mfgAdvanceItem._isAccept} onOpenChange={(open) => { if (!open) setMfgAdvanceItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="mfg-advance-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-purple-500" /> Manufacturing Advance Check
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3 text-[hsl(var(--muted-foreground))]">
              <p className="text-sm">Before advancing this item to QC, please confirm the following:</p>
              {mfgAdvanceItem && (
                <div className="px-3 py-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                  <p className="text-sm font-semibold">{mfgAdvanceItem.item_description}</p>
                  <p className="font-mono-data text-xs font-semibold text-[hsl(var(--foreground))]/80"><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{mfgAdvanceItem.po_number}</p>
                </div>
              )}
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" /><span>Drawings and specification documents have been reviewed</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" /><span>Control card has been prepared and signed off</span></div>
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" /><span>All dimensions are within tolerance</span></div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button onClick={() => setMfgAdvanceItem(null)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="mfg-advance-no-btn">Cancel</button>
            <button onClick={handleAdvanceDocsConfirm} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-colors" data-testid="mfg-advance-yes-btn">Confirmed — Advance to QC</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manufacturing: missing-ERP-code alert (soft gate before the advance check) */}
      <Dialog open={!!erpAlertItem} onOpenChange={(open) => { if (!open) { setErpAlertItem(null); setErpHighlightId(null); } }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="erp-alert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-500" /> ERP code missing
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-[hsl(var(--muted-foreground))]">
              You have not updated the ERP code, please update. Press “Don’t update” to proceed without updating.
            </DialogDescription>
          </DialogHeader>
          {erpAlertItem && (
            <div className="px-3 py-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
              <p className="text-sm font-semibold">{erpAlertItem.item_description}</p>
              <p className="font-mono-data text-xs font-semibold text-[hsl(var(--foreground))]/80">
                <span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">Part</span>{erpAlertItem.part_number || "—"}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={handleErpAlertSkip} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="erp-alert-skip-btn">Don’t update</button>
            <button onClick={handleErpAlertUpdate} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="erp-alert-update-btn">Update ERP code</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ERP code entry — one-time per part number; backend backfills siblings */}
      <Dialog open={!!erpDialogItem} onOpenChange={(open) => { if (!open) setErpDialogItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[420px] max-w-[92vw]" data-testid="erp-code-modal">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              {erpDialogItem?.erp_code ? "Change ERP codes" : "Set ERP codes"}
              {erpDialogItem?.part_number && <span className="font-mono-data text-[hsl(var(--muted-foreground))] font-medium">· {erpDialogItem.part_number}</span>}
            </DialogTitle>
            <DialogDescription className="text-xs text-[hsl(var(--muted-foreground))]">
              Per part number. Applied to {erpLineCount === 1 ? "this line and every future line" : `all ${erpLineCount} lines (and every future line)`} with this part number, across all POs. Add one per line, or paste many at once. Once set, only an Admin can change them.
              {erpDialogItem && erpDialogItem.stage !== "Manufacturing" && (
                <span className="block mt-1.5 text-amber-700 dark:text-amber-400">
                  This item is already in {erpDialogItem.stage} — ERP codes are normally entered during Manufacturing.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]" htmlFor="erp-code-input">ERP codes</label>
            <div
              className="mt-1 min-h-[88px] max-h-[180px] overflow-auto w-full p-2 rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] focus-within:ring-2 focus-within:ring-emerald-500/50 flex flex-wrap gap-1.5 content-start cursor-text"
              onClick={() => document.getElementById("erp-code-input")?.focus()}
              data-testid="erp-code-chipfield"
            >
              {erpCodes.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 font-mono-data text-xs font-semibold text-emerald-700 dark:text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-1 leading-none">
                  {c}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeErpCode(i); }}
                    className="opacity-70 hover:opacity-100 hover:text-red-600 transition-colors"
                    aria-label={`Remove ${c}`}
                    data-testid={`erp-code-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                id="erp-code-input"
                value={erpInput}
                onChange={handleErpInputChange}
                onKeyDown={handleErpInputKeyDown}
                onPaste={handleErpPaste}
                placeholder={erpCodes.length ? "Add another…" : "Type or paste, e.g. MTJW000212 MTJW000213"}
                autoFocus
                className="flex-1 min-w-[140px] bg-transparent font-mono-data text-sm leading-none py-1 focus:outline-none placeholder:text-[hsl(var(--muted-foreground))]/60"
                data-testid="erp-code-input"
              />
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Enter, space, or comma adds a code · paste a whole list at once</span>
              {erpCodes.length > 0 && <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">{erpCodes.length} code{erpCodes.length !== 1 ? "s" : ""}</span>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setErpDialogItem(null)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="erp-code-cancel-btn">Cancel</button>
            <button onClick={handleErpSave} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="erp-code-save-btn">
              {erpLineCount === 1 ? "Save" : `Save for all ${erpLineCount} lines`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manufacturing: Record Machine & Cycle Time */}
      <Dialog open={!!mfgMachiningItem} onOpenChange={(open) => { if (!open) setMfgMachiningItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="mfg-machining-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-purple-500" /> Record Machining Data
            </DialogTitle>
            <DialogDescription className="pt-2 text-[hsl(var(--muted-foreground))]">
              <p className="text-sm">Optionally record the machine used and cycle time before advancing to QC.</p>
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Machine Used</label>
              <input type="text" value={mfgMachineUsed} onChange={e => setMfgMachineUsed(e.target.value)} placeholder="e.g. CNC-3, VMC-1, Lathe-2"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="mfg-machine-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Cycle Time</label>
              <input type="text" value={mfgCycleTime} onChange={e => setMfgCycleTime(e.target.value)} placeholder="e.g. 4.5 hrs, 2 days"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="mfg-cycle-input" />
            </div>
          </div>
          <DialogFooter className="gap-2 px-6 pb-4">
            <button onClick={() => setMfgMachiningItem(null)} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="mfg-machining-skip-btn">Skip</button>
            <button onClick={handleMfgMachiningConfirm} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-colors" data-testid="mfg-machining-confirm-btn">Save & Advance</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch: Shipped — Carrier, Tracking & Ship Date */}
      <Dialog open={!!dispatchShippedItem} onOpenChange={(open) => { if (!open) setDispatchShippedItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="dispatch-shipped-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-500" /> Mark as Shipped
            </DialogTitle>
            <DialogDescription className="pt-2 text-[hsl(var(--muted-foreground))]">
              <p className="text-sm">Enter carrier, tracking, and shipment date details.</p>
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Carrier Name</label>
              <input type="text" value={dispatchCarrier} onChange={e => setDispatchCarrier(e.target.value)} placeholder="e.g. Blue Dart, DHL, DTDC"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="dispatch-carrier-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Tracking ID</label>
              <input type="text" value={dispatchTrackingId} onChange={e => setDispatchTrackingId(e.target.value)} placeholder="e.g. BD123456789"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="dispatch-tracking-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Shipped On</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dispatchShippedAt}
                  onChange={e => setDispatchShippedAt(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="flex-1 h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="dispatch-shipped-at-input"
                />
                <button
                  type="button"
                  onClick={() => setDispatchShippedAt(new Date().toISOString().slice(0, 10))}
                  className="h-9 px-3 text-xs font-semibold rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-amber-500/50 transition-colors whitespace-nowrap"
                  data-testid="dispatch-fill-today-btn"
                >
                  Fill as today
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 px-6 pb-4">
            <button onClick={() => setDispatchShippedItem(null)} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="dispatch-shipped-cancel-btn">Cancel</button>
            <button onClick={handleDispatchShippedConfirm} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors" data-testid="dispatch-shipped-confirm-btn">Confirm Shipped</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manufacturing: Third Party — Where & ETA popup */}
      <Dialog open={!!thirdPartyItem} onOpenChange={(open) => { if (!open) setThirdPartyItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="third-party-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-base">Third Party Details</span>
            </DialogTitle>
            <DialogDescription className="pt-1 text-[hsl(var(--muted-foreground))] text-sm">
              Enter where this item is being sent and the expected return date.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">Where <span className="text-[hsl(var(--muted-foreground))] font-normal normal-case tracking-normal">(optional)</span></label>
              <input
                type="text"
                value={thirdPartyWhere}
                onChange={e => setThirdPartyWhere(e.target.value)}
                placeholder="e.g. ABC Heat Treatment, Pune"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="third-party-where-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] block mb-1">ETA <span className="text-[hsl(var(--muted-foreground))] font-normal normal-case tracking-normal">(optional)</span></label>
              <input
                type="text"
                value={thirdPartyEta}
                onChange={e => setThirdPartyEta(e.target.value)}
                placeholder="e.g. 20 Jun'26 or 3–4 working days"
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="third-party-eta-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 px-6 pb-4">
            <button onClick={() => setThirdPartyItem(null)} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]" data-testid="third-party-cancel-btn">Cancel</button>
            <button onClick={handleThirdPartyConfirm} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-orange-600 hover:bg-orange-500 text-white transition-colors" data-testid="third-party-confirm-btn">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ETA Picker — opens when a procurement subteam status flips to "Ordered" */}
      <EtaPickerModal
        open={!!pendingEtaChange}
        item={pendingEtaChange?.item || null}
        taskName={pendingEtaChange?.taskName || ""}
        accent={
          pendingEtaChange
            ? PROC_TEAM_COLORS[pendingEtaChange.taskName]?.color || "#3b82f6"
            : "#3b82f6"
        }
        inrRate={settings?.inr_rate || 83}
        onConfirm={handleEtaConfirm}
        onCancel={() => setPendingEtaChange(null)}
        onCompleteFromStores={handleSourcingComplete}
        onKeepOrdered={handleSourcingOrdered}
        onSourcingChanged={refreshData}
      />

      {/* Spend audit history (add + soft-delete) */}
      <SpendHistoryModal
        open={!!spendHistoryItem}
        item={spendHistoryItem}
        canEdit={canEdit}
        onClose={() => setSpendHistoryItem(null)}
        onItemUpdate={handleSpendHistoryItemUpdate}
      />

      {/* Ready-for-Manufacturing lightbox — surfaces fully-procured items */}
      <ReadyForMfgLightbox
        open={readyForMfgOpen}
        items={readyItems}
        onClose={() => setReadyForMfgOpen(false)}
        advanceItem={advanceItem}
      />

      {/* Assemblies — Child Part Tracker + parts-list creator. Both read the
          LIVE item from global state so every save re-renders in place. */}
      <ChildPartTracker
        open={!!bomTrackerId}
        item={bomTrackerId ? allItems.find(i => i.id === bomTrackerId) : null}
        currentUser={user}
        perms={{ canDefine: canDefineBom, canBuy: canBuyBom, canViewCost: showSpend }}
        onClose={() => setBomTrackerId(null)}
        onItemUpdate={handleBomItemUpdate}
        onPasteMore={(it) => setBomCreatorId(it.id)}
      />
      <BomCreator
        open={!!bomCreatorId}
        item={bomCreatorId ? allItems.find(i => i.id === bomCreatorId) : null}
        currentUser={user}
        onClose={() => setBomCreatorId(null)}
        onSaved={handleBomItemUpdate}
      />

      {/* Back to single part — clears a never-ordered parts list */}
      <Dialog open={!!bomToSingleItem} onOpenChange={(o) => { if (!o) setBomToSingleItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="bom-to-single-modal">
          <DialogHeader>
            <DialogTitle>Make this a single part?</DialogTitle>
            <DialogDescription className="pt-1 text-sm text-[hsl(var(--muted-foreground))]">
              Its list of {bomSummary(bomToSingleItem?.bom).n} child part{bomSummary(bomToSingleItem?.bom).n === 1 ? "" : "s"} will be deleted. Nothing has been ordered yet, so nothing else changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button onClick={() => setBomToSingleItem(null)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]">Keep assembly</button>
            <button onClick={() => { const it = bomToSingleItem; setBomToSingleItem(null); saveBuildType(it, "single"); }} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors">Delete list, make single</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partially procured — move an assembly to Manufacturing early */}
      <Dialog open={!!partialAdvanceItem} onOpenChange={(o) => { if (!o) setPartialAdvanceItem(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="partial-advance-modal">
          {(() => {
            const it = partialAdvanceItem;
            const bs = bomSummary(it?.bom);
            const left = (it?.bom || []).filter(p => p.status !== "Received");
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Move to Manufacturing with {bs.received} of {bs.n} parts in?</DialogTitle>
                  <DialogDescription asChild>
                    <div className="pt-1 space-y-3 text-[hsl(var(--muted-foreground))]">
                      {it && (
                        <div className="px-3 py-2 rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">{it.item_description}</p>
                          <p className="font-mono-data text-xs font-semibold text-[hsl(var(--foreground))]/80"><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{it.po_number} · L{it.line_number}</p>
                        </div>
                      )}
                      <ul className="list-disc pl-5 space-y-1.5 text-sm">
                        <li>Still to come: {left.slice(0, 3).map(p => p.part_no || p.description.split(",")[0]).join(", ")}{left.length > 3 ? ` and ${left.length - 3} more` : ""}{bs.late ? ` (${bs.late} late)` : ""}</li>
                        <li>Manufacturing sees an orange <b className="text-orange-600 dark:text-orange-400">Partially procured</b> flag until the last part is in</li>
                        <li>Material keeps updating the Child Part Tracker from here</li>
                      </ul>
                    </div>
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <button onClick={() => setPartialAdvanceItem(null)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]">Cancel</button>
                  <button onClick={handlePartialAdvanceConfirm} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-colors" data-testid="partial-advance-yes-btn">Move with {bs.received} of {bs.n}</button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Return Reason Modal — Step 6: validated, min 6 chars required */}
      {returnReasonItem && (
        <Dialog open onOpenChange={(open) => { if (!open) { setReturnReasonItem(null); setReturnReason(""); setReturnIsRework(false); setReturnDrawingLinks([""]); } }}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md p-0 overflow-hidden" data-testid="return-reason-modal">
            <div className="h-1 w-full bg-gradient-to-r from-amber-500 to-orange-500" />
            <div className="px-6 pt-5 pb-2">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                    <ArrowLeft className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Send Back to Previous Stage</p>
                    <p className="text-xs font-normal text-[hsl(var(--muted-foreground))] mt-0.5 truncate max-w-[260px]">
                      {returnReasonItem.item_description}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>
            </div>
            <div className="px-6 py-3 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] block mb-1.5">
                  Reason for Sending Back{" "}
                  <span className="text-red-400 font-normal normal-case">(required, min 6 chars)</span>
                </label>
                <textarea
                  value={returnReason}
                  onChange={e => setReturnReason(e.target.value)}
                  rows={3}
                  placeholder="Describe why this item is being sent back..."
                  className="w-full px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="return-reason-textarea"
                  autoFocus
                />
                {returnReason.trim().length > 0 && returnReason.trim().length <= 5 && (
                  <p className="text-xs text-red-500 mt-1" data-testid="return-reason-error">Please provide at least 6 characters.</p>
                )}
              </div>

              {stage === "Quality Control" && (
                <button
                  onClick={() => setReturnIsRework(r => !r)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left ${returnIsRework ? "border-teal-500/40 bg-teal-500/10" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:border-teal-500/30"}`}
                  data-testid="return-rework-toggle"
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${returnIsRework ? "bg-teal-500 border-teal-500" : "border-[hsl(var(--border))]"}`}>
                    {returnIsRework && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${returnIsRework ? "text-teal-600" : "text-[hsl(var(--foreground))]"}`}>
                      <ReworkIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                      Mark as Rework
                    </p>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                      Tags item for rework — visible in Manufacturing as a Rework counter
                    </p>
                  </div>
                </button>
              )}

              {stage === "Quality Control" && (
                <div data-testid="return-drawing-links">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 mb-1.5">
                    <Link2 className="w-3 h-3" /> Drawing Links
                    <span className="text-[hsl(var(--muted-foreground))] font-normal normal-case tracking-normal">
                      (optional · OneDrive / SharePoint · up to 20)
                    </span>
                  </label>
                  <div className="space-y-1.5">
                    {returnDrawingLinks.map((url, idx) => {
                      const trimmed = url.trim();
                      const isBad = trimmed.length > 0 && !/^https?:\/\/.+\..+/i.test(trimmed);
                      return (
                        <div key={idx} className="flex items-center gap-1.5">
                          <div className="flex-1 relative">
                            <Link2 className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]/60 pointer-events-none" />
                            <input
                              type="url"
                              value={url}
                              onChange={e => {
                                const next = [...returnDrawingLinks];
                                next[idx] = e.target.value;
                                setReturnDrawingLinks(next);
                              }}
                              placeholder="https://rockpecker.sharepoint.com/…"
                              className={`w-full pl-8 pr-2 py-1.5 text-xs rounded-md bg-[hsl(var(--muted))] border text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors ${isBad ? "border-red-500/60" : "border-[hsl(var(--border))]"}`}
                              data-testid={`return-drawing-link-${idx}`}
                            />
                          </div>
                          {returnDrawingLinks.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setReturnDrawingLinks(returnDrawingLinks.filter((_, i) => i !== idx))}
                              className="p-1.5 rounded-md text-[hsl(var(--muted-foreground))] hover:bg-red-500/10 hover:text-red-500 transition-colors"
                              aria-label="Remove link"
                              data-testid={`return-drawing-link-remove-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <button
                      type="button"
                      disabled={returnDrawingLinks.length >= 20}
                      onClick={() => setReturnDrawingLinks([...returnDrawingLinks, ""])}
                      className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 hover:text-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid="return-drawing-link-add"
                    >
                      <Plus className="w-3 h-3" /> Add another link
                    </button>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono-data">
                      {returnDrawingLinksCleaned.length}/20
                    </span>
                  </div>
                  {returnDrawingLinksCleaned.length > 0 && !returnDrawingLinksValid && (
                    <p className="text-[11px] text-red-500 mt-1">Each link must start with http:// or https:// and include a domain.</p>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[hsl(var(--border))]/50 flex items-center justify-between">
              <button
                onClick={() => { setReturnReasonItem(null); setReturnReason(""); setReturnIsRework(false); setReturnDrawingLinks([""]); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors border border-[hsl(var(--border))]"
                data-testid="return-reason-cancel-btn"
              >Cancel</button>
              <button
                disabled={!returnReasonValid || !returnDrawingLinksValid || returningId === returnReasonItem.id}
                onClick={async () => {
                  setReturningId(returnReasonItem.id);
                  const item = returnReasonItem;
                  const links = returnDrawingLinksCleaned;
                  setReturnReasonItem(null);
                  await returnItem(item.id, returnReason, returnIsRework, links.length ? links : null);
                  setReturningId(null);
                  setReturnReason("");
                  setReturnIsRework(false);
                  setReturnDrawingLinks([""]);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${returnIsRework ? "bg-teal-600 hover:bg-teal-700" : "bg-amber-600 hover:bg-amber-700"}`}
                data-testid="return-reason-confirm-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {returnIsRework ? "Send Back & Mark Rework" : "Send Back"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── QC: return failed part to customer ── */}
      {rtcItem && (
        <Dialog open onOpenChange={(o) => { if (!o) setRtcItem(null); }}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="return-customer-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PackageX className="w-5 h-5 text-amber-500" /> Return failed part to customer
              </DialogTitle>
              <DialogDescription>
                Sends <span className="font-medium text-[hsl(var(--foreground))]">{rtcItem.item_description}</span> to Dispatch flagged as a QC-failed return. The original value stays on the books; the return value below is what it ships back at.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {showValue && typeof rtcItem.value_usd === "number" && (
                <div className="flex items-center justify-between rounded-md bg-[hsl(var(--muted))]/50 px-3 py-2">
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Original PO value (kept)</span>
                  <span className="text-sm font-mono-data">${rtcItem.value_usd.toLocaleString()}</span>
                </div>
              )}
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] block mb-1.5">Return value (USD) *</label>
                <input
                  type="number" min="0" step="any" value={rtcValue}
                  onChange={(e) => setRtcValue(e.target.value)}
                  className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] font-mono-data focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="return-customer-value"
                />
              </div>
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] block mb-1.5">Note to Dispatch *</label>
                <textarea
                  value={rtcNote} onChange={(e) => setRtcNote(e.target.value)}
                  rows={3} placeholder="e.g. Failed QC — raw material supplied by customer. Returned at $0."
                  className="w-full px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] resize-y focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="return-customer-note"
                />
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setRtcItem(null)}
                className="text-[13px] font-medium px-4 py-2 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] text-[hsl(var(--foreground))] transition-colors">Cancel</button>
              <button type="button"
                disabled={rtcSaving || !rtcNote.trim() || rtcValue === "" || Number(rtcValue) < 0 || Number.isNaN(Number(rtcValue))}
                onClick={async () => {
                  setRtcSaving(true);
                  const ok = await returnItemToCustomer(rtcItem.id, Number(rtcValue) || 0, rtcNote.trim());
                  setRtcSaving(false);
                  if (ok) setRtcItem(null);
                }}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white transition-colors"
                data-testid="return-customer-confirm">
                {rtcSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageX className="w-4 h-4" />}
                Send to dispatch
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

